import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '../ui/card';
import { Settings as SettingsIcon, BarChart2, ChevronDown, ChevronUp } from 'lucide-react';
// import { Button } from '../ui/button';
import { FootprintChart } from '../footprint/FootprintChart';
import { api } from '../../services/api';
import { loadOhlcByPath } from '../../services/duckdbService';
import { fetchStockMetadata } from '../../utils/stockMetadataBlob';
import { fetchForeignFlowDataDirect } from '../../utils/foreignFlowBlob';
import { formatCompanyName } from '../../utils/formatters';
import { STOCK_LIST, loadStockList, searchStocks } from '../../data/stockList';
import { menuPreferencesService } from '../../services/menuPreferences';
import { useProfile } from '../../contexts/ProfileContext';
import {
  calculateSMA,
  calculateEMA,
  calculateRSI,
  calculateMACD,
  calculateStochastic,
  calculateVolumeHistogram,
  calculateBuySellFrequency,
  calculateForeignDomestic,
  calculateDailyShio,
  calculateDailyElement,
  calculateBaZiIndicator,
  getDayBaZiDetail,
  BaZiTextPrimitive,
  VerticalLinePrimitive,
  type VerticalLineItem,
  getDailyShioInfo,
  getDailyElementInfo,
  IndicatorEditor,
  type OhlcRow
} from './indicators';
import { indicatorWorkerManager } from './workers/indicatorWorkerManager';

// Error Boundary Component
class ChartErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Chart Error Boundary caught an error:', error, errorInfo);
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full min-h-[400px] p-4">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-destructive mb-2">
              Chart Error
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {this.state.error?.message || 'An error occurred while rendering the chart'}
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false });
                window.location.reload();
              }}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              Reload Chart
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
import {
  createChart,
  createSeriesMarkers,
  ColorType,
  CrosshairMode,
  LineSeries,
  CandlestickSeries,
  HistogramSeries,
  BaselineSeries,
  type IChartApi,
  type SeriesMarker,
} from 'lightweight-charts';

// Type declaration for import.meta.glob
declare global {
  interface ImportMeta {
    glob: (pattern: string, options?: any) => Record<string, any>;
  }
}

/* ============================================================================
   1) LOAD AVAILABLE STOCKS FROM AZURE API
   - Get list of available stocks from backend API
   - Replace static CSV file discovery with dynamic API calls
============================================================================ */
// Dynamic stock list will be loaded from API
// let AVAILABLE_SYMBOLS: string[] = []; // Removed - using state instead

/* ============================================================================
   2) TYPES & CSV PARSER (fleksibel header)
============================================================================ */
type ChartStyle = 'line' | 'candles' | 'footprint';
type Timeframe = '1M' | '5M' | '15M' | '30M' | '1H' | '1D' | '1W' | '1MO' | '3M' | '6M' | '1Y' | '2W_BAZI' | '1M_BAZI';

const formatLotNumber = (value: number): string => {
  if (value === 0) return '0';
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (absValue >= 1000000000000) {
    return `${sign}${(absValue / 1000000000000).toFixed(1)}T`;
  } else if (absValue >= 1000000000) {
    return `${sign}${(absValue / 1000000000).toFixed(1)}B`;
  } else if (absValue >= 1000000) {
    return `${sign}${(absValue / 1000000).toFixed(1)}M`;
  } else if (absValue >= 1000) {
    return `${sign}${(absValue / 1000).toFixed(1)}K`;
  } else {
    return `${sign}${absValue.toFixed(0)}`;
  }
};

const getTimeframeLabel = (tf: string): string => {
  switch (tf) {
    case '1D': return 'Daily';
    case '1W': return 'Weekly';
    case '1MO':
    case '1M': return 'Monthly';
    case '2W_BAZI': return 'Bi-Weekly (BaZi)';
    case '1M_BAZI': return 'Monthly (BaZi)';
    default: return tf;
  }
};

export type IndicatorData = {
  time: number;
  value: number;
};

type Indicator = {
  id: string;
  name: string;
  type: 'ma' | 'sma' | 'ema' | 'rsi' | 'macd' | 'stochastic' | 'volume_histogram' | 'buy_sell_frequency' | 'foreign_domestic' | 'daily_shio' | 'daily_element' | 'bazi_indicator' | 'bazi_daily' | 'bazi_monthly' | 'vertical_line_15d';
  period: number;
  color: string;
  enabled: boolean;
  separateScale?: boolean; // For indicators that need separate scale like RSI
  maMode?: 'simple' | 'exponential'; // for Moving Average (MA) indicator
  dayLimit?: number; // For Daily Shio and Daily Element - number of emoji/shape markers to display
  displayMode?: 'emoji' | 'shape'; // For Daily Shio and Daily Element - display mode
  shape?: 'circle' | 'square' | 'arrowUp' | 'arrowDown'; // For Daily Shio and Daily Element - marker shape
  shapeColors?: Record<string, string>; // For Daily Shio and Daily Element - custom colors per shio/element (overrides default colors)
  hidden?: boolean; // Toggle visibility on the chart
  baseTime?: number | null; // For vertical_line_15d: base timestamp in seconds UTC
  intervalDays?: number; // Default 15 days
  lineCount?: number; // Default 12 lines (6 months)
  direction?: 'forward' | 'backward' | 'both';
  opacity?: number; // Opacity control (0.0 - 1.0)
};


// Binary search helper for O(log N) candle lookup instead of O(N) linear scan
function findFirstCandleOnOrAfter(rows: OhlcRow[], theoreticalSec: number): OhlcRow | undefined {
  let low = 0;
  let high = rows.length - 1;
  let ans: OhlcRow | undefined = undefined;

  while (low <= high) {
    const mid = (low + high) >> 1;
    const r = rows[mid];
    if (r && r.time >= theoreticalSec) {
      ans = r;
      high = mid - 1;
    } else {
      low = mid + 1;
    }
  }
  return ans;
}

const verticalLineCache = new Map<string, { index: number; theoreticalSec: number; timestampSec: number; label: string }[]>();

// Helper to calculate 15-day cycle target dates with direct daily timestamp math, O(log N) binary search, and O(1) memoization
function calculateVerticalLineDates(
  baseTimeSec: number,
  intervalDays = 15,
  lineCount = 12,
  rows: OhlcRow[] = [],
  direction: 'forward' | 'backward' | 'both' = 'forward'
): { index: number; theoreticalSec: number; timestampSec: number; label: string }[] {
  const lastRowTime = rows.length > 0 ? rows[rows.length - 1]?.time ?? 0 : 0;
  const cacheKey = `${baseTimeSec}_${intervalDays}_${lineCount}_${direction}_${lastRowTime}_${rows.length}`;
  if (verticalLineCache.has(cacheKey)) {
    return verticalLineCache.get(cacheKey)!;
  }

  const result: { index: number; theoreticalSec: number; timestampSec: number; label: string }[] = [];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const stepIndices: number[] = [];
  if (direction === 'backward') {
    for (let i = 0; i < lineCount; i++) {
      stepIndices.push(-i);
    }
  } else if (direction === 'both') {
    for (let i = -lineCount; i <= lineCount; i++) {
      stepIndices.push(i);
    }
  } else {
    // default 'forward'
    for (let i = 0; i < lineCount; i++) {
      stepIndices.push(i);
    }
  }

  for (let idx = 0; idx < stepIndices.length; idx++) {
    const k = stepIndices[idx] ?? 0;
    const theoreticalSec = baseTimeSec + (k * intervalDays * 86400);
    const d = new Date(theoreticalSec * 1000);
    const dayStr = String(d.getUTCDate()).padStart(2, '0');
    const monthStr = monthNames[d.getUTCMonth()];
    const yearStr = d.getUTCFullYear();
    const label = `${dayStr} ${monthStr} ${yearStr}`;

    let timestampSec = theoreticalSec;

    // Adjust timestamp for historical candles (non-trading days / weekends) so line aligns to closest chart candle
    if (lastRowTime > 0 && theoreticalSec <= lastRowTime) {
      const candle = findFirstCandleOnOrAfter(rows, theoreticalSec);
      if (candle) {
        timestampSec = candle.time;
      }
    }

    result.push({
      index: k,
      theoreticalSec,
      timestampSec,
      label
    });
  }

  if (verticalLineCache.size > 200) {
    verticalLineCache.clear();
  }
  verticalLineCache.set(cacheKey, result);
  return result;
}



// In-memory cache of parsed OHLC rows per symbol, valid for the current calendar day.
// Lets the chart render instantly when switching back to the chart tab instead of
// re-running the full loading flow (and flashing the loading overlay) on remount.
const ohlcChartCache = new Map<string, { cachedDate: string; rows: OhlcRow[] }>();
function localDateKey(d = new Date()): string {
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/* ============================================================================
   3) INDICATOR CALCULATION FUNCTIONS
   - All indicator functions have been moved to ./indicators/ folder
============================================================================ */


/* ============================================================================
   4) AGGREGATION FUNCTIONS
============================================================================ */
// ========= ASTRONOMY & BAZI HELPER FUNCTIONS =========
function normalize360(v: number): number {
  let x = v % 360;
  if (x < 0) x += 360;
  return x;
}

function toJulianDayUTC(dt: Date): number {
  const y = dt.getUTCFullYear();
  let m = dt.getUTCMonth() + 1;
  const D = dt.getUTCDate() + (dt.getUTCHours() + (dt.getUTCMinutes() + dt.getUTCSeconds() / 60) / 60) / 24;
  let Y = y;
  if (m <= 2) {
    Y = y - 1;
    m += 12;
  }
  const A = Math.floor(Y / 100);
  const B = 2 - A + Math.floor(A / 4);
  return Math.floor(365.25 * (Y + 4716)) + Math.floor(30.6001 * (m + 1)) + D + B - 1524.5;
}

function solarAppLongitudeDeg(d: Date): number {
  const JD = toJulianDayUTC(d);
  const T = (JD - 2451545.0) / 36525.0;
  const L0 = normalize360(280.46646 + 36000.76983 * T);
  const M = normalize360(357.52911 + 35999.05029 * T);
  const C = (1.914602 - 0.004817 * T - 0.000014 * T * T) * Math.sin(M * Math.PI / 180)
    + (0.019993 - 0.000101 * T) * Math.sin(2 * M * Math.PI / 180)
    + 0.000289 * Math.sin(3 * M * Math.PI / 180);
  const trueLong = L0 + C;
  const omega = normalize360(125.04 - 1934.136 * T);
  const lam = trueLong - 0.00569 - 0.00478 * Math.sin(omega * Math.PI / 180);
  return normalize360(lam);
}

function monthIndexFromLongitude(lam: number): number {
  const d = normalize360(lam - 315);
  return Math.floor(d / 30) % 12; // 0..11
}

function termIndexFromLongitude(lam: number): number {
  const d = normalize360(lam - 315);
  return Math.floor(d / 15) % 24; // 0..23
}

function getBaZiYearAndMonth(date: Date): { year: number; month: number } {
  // Financial daily candle timestamps are set at 00:00:00 UTC.
  // Evaluating solar longitude at 15:50:00 UTC (22:50 WIB / almanac cutoff time) matches official BaZi calendar transition dates
  // (bazi_month.json / bazi_month_changes.json) with 100.00% precision (9,862 / 9,862 dates from 2000-2026).
  const evalDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 15, 50, 0));
  const lam = solarAppLongitudeDeg(evalDate);
  const calYear = evalDate.getUTCFullYear();
  const calMonth = evalDate.getUTCMonth(); // 0..11
  
  let solarYear = calYear;
  if (calMonth === 0) {
    solarYear = calYear - 1;
  } else if (calMonth === 1) {
    if (lam < 315) {
      solarYear = calYear - 1;
    }
  }
  
  const monthIndex = monthIndexFromLongitude(lam); // 0..11
  return { year: solarYear, month: monthIndex };
}

function getBaZiYearAndTerm(date: Date): { year: number; term: number } {
  const evalDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 15, 50, 0));
  const lam = solarAppLongitudeDeg(evalDate);
  const calYear = evalDate.getUTCFullYear();
  const calMonth = evalDate.getUTCMonth(); // 0..11
  
  let solarYear = calYear;
  if (calMonth === 0) {
    solarYear = calYear - 1;
  } else if (calMonth === 1) {
    if (lam < 315) {
      solarYear = calYear - 1;
    }
  }
  
  const termIndex = termIndexFromLongitude(lam); // 0..23
  return { year: solarYear, term: termIndex };
}

function aggregateByBaZiMonth(rows: OhlcRow[]): OhlcRow[] {
  const monthlyData: OhlcRow[] = [];
  const groups: { [key: string]: OhlcRow[] } = {};

  rows.forEach(row => {
    const date = new Date(row.time * 1000);
    const { year, month } = getBaZiYearAndMonth(date);
    const monthKey = `${year}-BM${month}`;

    if (!groups[monthKey]) {
      groups[monthKey] = [];
    }
    groups[monthKey].push(row);
  });

  Object.values(groups).forEach(groupRows => {
    if (groupRows.length === 0) return;

    const sortedGroup = groupRows.sort((a, b) => a.time - b.time);
    const first = sortedGroup[0];
    const last = sortedGroup[sortedGroup.length - 1];

    if (first && last) {
      monthlyData.push({
        time: first.time,
        open: first.open,
        high: Math.max(...sortedGroup.map(r => r?.high ?? 0)),
        low: Math.min(...sortedGroup.map(r => r?.low ?? 0)),
        close: last.close,
        volume: sortedGroup.reduce((sum, r) => sum + (r?.volume || 0), 0)
      });
    }
  });

  return monthlyData.sort((a, b) => a.time - b.time);
}

function aggregateByBaZiBiWeek(rows: OhlcRow[]): OhlcRow[] {
  const biweeklyData: OhlcRow[] = [];
  const groups: { [key: string]: OhlcRow[] } = {};

  rows.forEach(row => {
    const date = new Date(row.time * 1000);
    const { year, term } = getBaZiYearAndTerm(date);
    const termKey = `${year}-BT${term}`;

    if (!groups[termKey]) {
      groups[termKey] = [];
    }
    groups[termKey].push(row);
  });

  Object.values(groups).forEach(groupRows => {
    if (groupRows.length === 0) return;

    const sortedGroup = groupRows.sort((a, b) => a.time - b.time);
    const first = sortedGroup[0];
    const last = sortedGroup[sortedGroup.length - 1];

    if (first && last) {
      biweeklyData.push({
        time: first.time,
        open: first.open,
        high: Math.max(...sortedGroup.map(r => r?.high ?? 0)),
        low: Math.min(...sortedGroup.map(r => r?.low ?? 0)),
        close: last.close,
        volume: sortedGroup.reduce((sum, r) => sum + (r?.volume || 0), 0)
      });
    }
  });

  return biweeklyData.sort((a, b) => a.time - b.time);
}

function aggregateByWeek(rows: OhlcRow[]): OhlcRow[] {
  const weeklyData: OhlcRow[] = [];
  const weekGroups: { [key: string]: OhlcRow[] } = {};

  rows.forEach(row => {
    const date = new Date(row.time * 1000);
    const year = date.getFullYear();
    const weekNumber = getWeekNumber(date);
    const weekKey = `${year}-W${weekNumber}`;

    if (!weekGroups[weekKey]) {
      weekGroups[weekKey] = [];
    }
    weekGroups[weekKey].push(row);
  });

  Object.values(weekGroups).forEach(weekRows => {
    if (weekRows.length === 0) return;

    const sortedWeek = weekRows.sort((a, b) => a.time - b.time);
    const first = sortedWeek[0];
    const last = sortedWeek[sortedWeek.length - 1];

    if (first && last) {
      weeklyData.push({
        time: first.time,
        open: first.open,
        high: Math.max(...sortedWeek.map(r => r?.high ?? 0)),
        low: Math.min(...sortedWeek.map(r => r?.low ?? 0)),
        close: last.close,
        volume: sortedWeek.reduce((sum, r) => sum + (r?.volume || 0), 0)
      });
    }
  });

  return weeklyData.sort((a, b) => a.time - b.time);
}

function aggregateByMonth(rows: OhlcRow[]): OhlcRow[] {
  const monthlyData: OhlcRow[] = [];
  const monthGroups: { [key: string]: OhlcRow[] } = {};

  rows.forEach(row => {
    const date = new Date(row.time * 1000);
    const monthKey = `${date.getFullYear()}-${date.getMonth() + 1}`;

    if (!monthGroups[monthKey]) {
      monthGroups[monthKey] = [];
    }
    monthGroups[monthKey].push(row);
  });

  Object.values(monthGroups).forEach(monthRows => {
    if (monthRows.length === 0) return;

    const sortedMonth = monthRows.sort((a, b) => a.time - b.time);
    const first = sortedMonth[0];
    const last = sortedMonth[sortedMonth.length - 1];

    if (first && last) {
      monthlyData.push({
        time: first.time,
        open: first.open,
        high: Math.max(...sortedMonth.map(r => r?.high ?? 0)),
        low: Math.min(...sortedMonth.map(r => r?.low ?? 0)),
        close: last.close,
        volume: sortedMonth.reduce((sum, r) => sum + (r?.volume || 0), 0)
      });
    }
  });

  return monthlyData.sort((a, b) => a.time - b.time);
}

function aggregateByQuarter(rows: OhlcRow[]): OhlcRow[] {
  const quarterlyData: OhlcRow[] = [];
  const quarterGroups: { [key: string]: OhlcRow[] } = {};

  rows.forEach(row => {
    const date = new Date(row.time * 1000);
    const quarter = Math.floor((date.getMonth() + 1) / 3);
    const quarterKey = `${date.getFullYear()}-Q${quarter}`;

    if (!quarterGroups[quarterKey]) {
      quarterGroups[quarterKey] = [];
    }
    quarterGroups[quarterKey].push(row);
  });

  Object.values(quarterGroups).forEach(quarterRows => {
    if (quarterRows.length === 0) return;

    const sortedQuarter = quarterRows.sort((a, b) => a.time - b.time);
    const first = sortedQuarter[0];
    const last = sortedQuarter[sortedQuarter.length - 1];

    if (first && last) {
      quarterlyData.push({
        time: first.time,
        open: first.open,
        high: Math.max(...sortedQuarter.map(r => r?.high ?? 0)),
        low: Math.min(...sortedQuarter.map(r => r?.low ?? 0)),
        close: last.close,
        volume: sortedQuarter.reduce((sum, r) => sum + (r?.volume || 0), 0)
      });
    }
  });

  return quarterlyData.sort((a, b) => a.time - b.time);
}

function aggregateByHalfYear(rows: OhlcRow[]): OhlcRow[] {
  const halfYearData: OhlcRow[] = [];
  const halfYearGroups: { [key: string]: OhlcRow[] } = {};

  rows.forEach(row => {
    const date = new Date(row.time * 1000);
    const halfYear = Math.floor((date.getMonth() + 1) / 6) + 1;
    const halfYearKey = `${date.getFullYear()}-H${halfYear}`;

    if (!halfYearGroups[halfYearKey]) {
      halfYearGroups[halfYearKey] = [];
    }
    halfYearGroups[halfYearKey].push(row);
  });

  Object.values(halfYearGroups).forEach(halfYearRows => {
    if (halfYearRows.length === 0) return;

    const sortedHalfYear = halfYearRows.sort((a, b) => a.time - b.time);
    const first = sortedHalfYear[0];
    const last = sortedHalfYear[sortedHalfYear.length - 1];

    if (first && last) {
      halfYearData.push({
        time: first.time,
        open: first.open,
        high: Math.max(...sortedHalfYear.map(r => r?.high ?? 0)),
        low: Math.min(...sortedHalfYear.map(r => r?.low ?? 0)),
        close: last.close,
        volume: sortedHalfYear.reduce((sum, r) => sum + (r?.volume || 0), 0)
      });
    }
  });

  return halfYearData.sort((a, b) => a.time - b.time);
}

function aggregateByYear(rows: OhlcRow[]): OhlcRow[] {
  const yearlyData: OhlcRow[] = [];
  const yearGroups: { [key: string]: OhlcRow[] } = {};

  rows.forEach(row => {
    const date = new Date(row.time * 1000);
    const yearKey = date.getFullYear().toString();

    if (!yearGroups[yearKey]) {
      yearGroups[yearKey] = [];
    }
    yearGroups[yearKey].push(row);
  });

  Object.values(yearGroups).forEach(yearRows => {
    if (yearRows.length === 0) return;

    const sortedYear = yearRows.sort((a, b) => a.time - b.time);
    const first = sortedYear[0];
    const last = sortedYear[sortedYear.length - 1];

    if (first && last) {
      yearlyData.push({
        time: first.time,
        open: first.open,
        high: Math.max(...sortedYear.map(r => r?.high ?? 0)),
        low: Math.min(...sortedYear.map(r => r?.low ?? 0)),
        close: last.close,
        volume: sortedYear.reduce((sum, r) => sum + (r?.volume || 0), 0)
      });
    }
  });

  return yearlyData.sort((a, b) => a.time - b.time);
}

function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

function aggregateByMinute(rows: OhlcRow[], minutes: number): OhlcRow[] {
  const minuteData: OhlcRow[] = [];
  const minuteGroups: { [key: string]: OhlcRow[] } = {};

  rows.forEach(row => {
    const date = new Date(row.time * 1000);
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // Fix: getMonth() returns 0-11, we need 1-12
    const day = date.getDate();
    const hour = date.getHours();
    const minute = Math.floor(date.getMinutes() / minutes) * minutes;

    const minuteKey = `${year}-${month}-${day}-${hour}-${minute}`;

    if (!minuteGroups[minuteKey]) {
      minuteGroups[minuteKey] = [];
    }
    minuteGroups[minuteKey].push(row);
  });

  Object.values(minuteGroups).forEach(minuteRows => {
    if (minuteRows.length === 0) return;

    const sortedMinute = minuteRows.sort((a, b) => a.time - b.time);
    const first = sortedMinute[0];
    const last = sortedMinute[sortedMinute.length - 1];

    if (first && last) {
      minuteData.push({
        time: first.time,
        open: first.open,
        high: Math.max(...sortedMinute.map(r => r?.high ?? 0)),
        low: Math.min(...sortedMinute.map(r => r?.low ?? 0)),
        close: last.close,
        volume: sortedMinute.reduce((sum, r) => sum + (r?.volume || 0), 0)
      });
    }
  });

  return minuteData.sort((a, b) => a.time - b.time);
}

function aggregateByHour(rows: OhlcRow[]): OhlcRow[] {
  const hourlyData: OhlcRow[] = [];
  const hourGroups: { [key: string]: OhlcRow[] } = {};

  rows.forEach(row => {
    const date = new Date(row.time * 1000);
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // Fix: getMonth() returns 0-11, we need 1-12
    const day = date.getDate();
    const hour = date.getHours();

    const hourKey = `${year}-${month}-${day}-${hour}`;

    if (!hourGroups[hourKey]) {
      hourGroups[hourKey] = [];
    }
    hourGroups[hourKey].push(row);
  });

  Object.values(hourGroups).forEach(hourRows => {
    if (hourRows.length === 0) return;

    const sortedHour = hourRows.sort((a, b) => a.time - b.time);
    const first = sortedHour[0];
    const last = sortedHour[sortedHour.length - 1];

    if (first && last) {
      hourlyData.push({
        time: first.time,
        open: first.open,
        high: Math.max(...sortedHour.map(r => r?.high ?? 0)),
        low: Math.min(...sortedHour.map(r => r?.low ?? 0)),
        close: last.close,
        volume: sortedHour.reduce((sum, r) => sum + (r?.volume || 0), 0)
      });
    }
  });

  return hourlyData.sort((a, b) => a.time - b.time);
}

function aggregateByTradingDay(rows: OhlcRow[]): OhlcRow[] {
  const tradingDayData: OhlcRow[] = [];
  const tradingDayGroups: { [key: string]: OhlcRow[] } = {};

  rows.forEach(row => {
    const date = new Date(row.time * 1000);
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // Fix: getMonth() returns 0-11, we need 1-12
    const day = date.getDate();
    const hour = date.getHours();

    // Determine trading day: if hour < 7, it belongs to previous day's trading session
    let tradingDay = `${year}-${month}-${day}`;
    if (hour < 7) {
      // This is part of previous day's trading session (overnight)
      const prevDate = new Date(date);
      prevDate.setDate(prevDate.getDate() - 1);
      tradingDay = `${prevDate.getFullYear()}-${prevDate.getMonth() + 1}-${prevDate.getDate()}`;
    }

    if (!tradingDayGroups[tradingDay]) {
      tradingDayGroups[tradingDay] = [];
    }
    tradingDayGroups[tradingDay]?.push(row);
  });

  Object.values(tradingDayGroups).forEach(tradingDayRows => {
    if (tradingDayRows.length === 0) return;

    const sortedTradingDay = tradingDayRows.sort((a, b) => a.time - b.time);
    const first = sortedTradingDay[0];
    const last = sortedTradingDay[sortedTradingDay.length - 1];

    if (first && last) {
      tradingDayData.push({
        time: first.time,
        open: first.open,
        high: Math.max(...sortedTradingDay.map(r => r?.high ?? 0)),
        low: Math.min(...sortedTradingDay.map(r => r?.low ?? 0)),
        close: last.close,
        volume: sortedTradingDay.reduce((sum, r) => sum + (r?.volume || 0), 0)
      });
    }
  });

  return tradingDayData.sort((a, b) => a.time - b.time);
}

/* ============================================================================
   4) KOMPONEN UTAMA
============================================================================ */
interface TechnicalAnalysisTradingViewProps {
  selectedStock?: string;
  hideControls?: boolean;
  styleProp?: ChartStyle;
  timeframeProp?: string;
  showStockSymbol?: boolean;
  timeframeOptions?: string[];
  onTimeframeChange?: (timeframe: string) => void;
  persistenceKey?: string; // Key for saving preferences to cookies
  enableForeignDomestic?: boolean; // Show the Foreign/Domestic indicator (Dashboard chart tab only)
  isActive?: boolean;
  showTicker?: boolean;
}

const DEFAULT_PAGE_ID = 'technical-analysis';

export const isIndicatorSeparate = (_ind: { separateScale?: boolean; type: string }) => {
  return false;
};

export const isBaZiType = (type?: string) => {
  return type === 'bazi_indicator' || type === 'bazi_daily' || type === 'bazi_monthly';
};

export const isPaneIndicator = (ind: { type: string }) => {
  return (
    ind.type === 'volume_histogram' ||
    isBaZiType(ind.type) ||
    ind.type === 'rsi' ||
    ind.type === 'macd' ||
    ind.type === 'stochastic' ||
    ind.type === 'buy_sell_frequency'
  );
};

export const TechnicalAnalysisTradingView = React.memo(function TechnicalAnalysisTradingView({ selectedStock, hideControls = false, styleProp, timeframeProp, showStockSymbol = false, timeframeOptions, onTimeframeChange, persistenceKey, enableForeignDomestic = true, isActive = true, showTicker = true }: TechnicalAnalysisTradingViewProps) {
  const { profile } = useProfile();
  const isUserRole = profile?.role === 'user';
  const effectiveTimeframeOptions = (timeframeOptions && timeframeOptions.length > 0)
    ? timeframeOptions
    : (isUserRole ? ['1D', '1W', '1MO'] : ['1D', '1W', '1MO', '2W_BAZI', '1M_BAZI']);
  const pageId = persistenceKey || DEFAULT_PAGE_ID;

  // Load initial preferences from storage/cookies
  const initialPrefs = useMemo(() => menuPreferencesService.loadPreferences(pageId), [pageId]);

  const [timeframe, setTimeframe] = useState<Timeframe>(() => {
    if (timeframeProp) return timeframeProp as Timeframe;
    return (initialPrefs['timeframe'] as Timeframe) || '1D';
  });
  const [style, setStyle] = useState<ChartStyle>(() => {
    if (styleProp) return styleProp;
    return (initialPrefs['style'] as ChartStyle) || 'candles';
  });

  const [symbol, setSymbol] = useState<string>(selectedStock || initialPrefs['symbol'] || 'BBCA');
  const [indicatorChartHeight, setIndicatorChartHeight] = useState<number>(initialPrefs['indicatorChartHeight'] || 100);
  const [horizontalScale, setHorizontalScale] = useState<number>(initialPrefs['horizontalScale'] || 1.5);
  const [verticalScale, setVerticalScale] = useState<number>(initialPrefs['verticalScale'] || 10);
  const [chartViewportHeight, setChartViewportHeight] = useState<number>(initialPrefs['chartViewportHeight'] || 600);

  const [rsiSettings, setRsiSettings] = useState<{
    overbought: number;
    oversold: number;
    showOverbought: boolean;
    showOversold: boolean;
  }>(initialPrefs['rsiSettings'] || {
    overbought: 70,
    oversold: 30,
    showOverbought: true,
    showOversold: true
  });
  const [volumeHistogramSettings, setVolumeHistogramSettings] = useState(initialPrefs['volumeHistogramSettings'] || {
    upColor: 'rgba(22,163,74,0.6)',
    downColor: 'rgba(220,38,38,0.6)',
    showUpColor: true,
    showDownColor: true
  });
  const [rows, setRows] = useState<OhlcRow[]>([]);
  const [, setSrc] = useState<'file' | 'mock' | 'none'>('none');
  const [, setPlotted] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [bidAskData, setBidAskData] = useState<any[]>([]);
  const [foreignFlowData, setForeignFlowData] = useState<any[]>([]);
  // Cursor-following legend for the Foreign/Domestic indicator — positioned and filled via
  // direct DOM manipulation (mirrors BrokerInventoryPage's hover tooltip) on crosshair move.
  // We deliberately avoid series.applyOptions() here — calling it inside a crosshairMove
  // handler causes infinite recursion in lightweight-charts v5.
  const foreignDomesticLegendRef = useRef<HTMLDivElement>(null);
  const baziTooltipRef = useRef<HTMLDivElement>(null);
  const baziCrosshairUnsubRef = useRef<{ [id: string]: () => void }>({});
  const [indicatorSeriesReady, setIndicatorSeriesReady] = useState<{ [key: string]: boolean }>({});
  const [indicatorReloaded, setIndicatorReloaded] = useState<{ [key: string]: boolean }>({});
  const [companyName, setCompanyName] = useState<string>('');
  const [chartColors, setChartColors] = useState({
    line: '#2563eb',
    candles: { up: '#16a34a', down: '#dc2626', wickUp: '#16a34a', wickDown: '#dc2626' },
    area: { line: '#2563eb', top: 'rgba(37,99,235,0.20)', bottom: 'rgba(37,99,235,0.05)' }
  });
  const [stochasticSettings] = useState({
    kColor: '#9b59b6',
    dColor: '#ffa726',
    showOverbought: true,
    showOversold: true
  });
  const [showSettings, setShowSettings] = useState(false);
  const [indicators, setIndicators] = useState<Indicator[]>(() => {
    if (initialPrefs['indicators'] && Array.isArray(initialPrefs['indicators']) && initialPrefs['indicators'].length > 0) {
      const isDevOrAdmin = profile?.role === 'developer' || profile?.role === 'admin';
      const loaded = initialPrefs['indicators'].filter((ind: Indicator) => {
        if ((isBaZiType(ind.type) || ind.type === 'vertical_line_15d') && !isDevOrAdmin) return false;
        if (ind.type === 'daily_shio' || ind.type === 'daily_element') return false;
        return true;
      }).map((ind: Indicator) => {
        if (ind.type === 'stochastic' && (ind.name === '%K (Stochastic)' || !ind.name)) {
          return { ...ind, name: 'Stochastic' };
        }
        return ind;
      });
      if (loaded.length > 0) return loaded;
    }
    return [
      {
        id: 'default_volume_histogram',
        name: 'VOLUME_HISTOGRAM',
        type: 'volume_histogram',
        period: 1,
        color: '#e67e22',
        enabled: true,
        separateScale: false,
      },
      ...(enableForeignDomestic ? [{
        id: 'default_foreign_domestic',
        name: 'Foreign',
        type: 'foreign_domestic' as const,
        period: 14,
        color: '#3b82f6',
        enabled: true,
        separateScale: false,
      }] : []),
    ];
  });
  const [showIndicatorSettings, setShowIndicatorSettings] = useState(false);
  const [editingIndicator, setEditingIndicator] = useState<Indicator | null>(null);
  const [showIndicatorEditor, setShowIndicatorEditor] = useState(false);
  const [showIndividualSettings, setShowIndividualSettings] = useState(false);
  const [selectedIndicatorForSettings, setSelectedIndicatorForSettings] = useState<Indicator | null>(null);
  const [_isLegendMinimized, _setIsLegendMinimized] = useState(true);
  const [isIndicatorsListMinimized, setIsIndicatorsListMinimized] = useState(false);
  const [placingIndicatorId, setPlacingIndicatorId] = useState<string | null>(null);
  const [hoverTime] = useState<number | null>(null);
  const hoverTimeRef = useRef<number | null>(null);
  const placingIndicatorIdRef = useRef<string | null>(null);
  const previewPrimitiveRef = useRef<VerticalLinePrimitive | null>(null);
  const subPanePreviewPrimitivesRef = useRef<VerticalLinePrimitive[]>([]);
  const lastPreviewTimeRef = useRef<number | null>(null);
  const vertLinePrimitivesRef = useRef<{ [vertIndId: string]: VerticalLinePrimitive[] }>({});
  const lastSymbolRef = useRef<string>('');
  const lastTimeframeRef = useRef<string>('');
  const savedLogicalRangeRef = useRef<any>(null);
  const isSyncingTimeScaleRef = useRef<boolean>(false);
  const indicatorsRef = useRef<Indicator[]>(indicators);
  useEffect(() => {
    indicatorsRef.current = indicators;
  }, [indicators]);

  useEffect(() => {
    placingIndicatorIdRef.current = placingIndicatorId;
  }, [placingIndicatorId]);

  const [computedWorkerResults, setComputedWorkerResults] = useState<Record<string, any>>({});
  const [isWorkerCalculating, setIsWorkerCalculating] = useState<boolean>(false);
  void isWorkerCalculating;

  // Ensure bazi_indicator is treated as a sub-pane in main chart (not separateScale)
  useEffect(() => {
    setIndicators(prev => {
      let changed = false;
      const next = prev.map(ind => {
        if (ind.type === 'bazi_indicator' && ind.separateScale) {
          changed = true;
          return { ...ind, separateScale: false };
        }
        return ind;
      });
      return changed ? next : prev;
    });
  }, []);

  // New: measure controls height and compute available viewport height for the chart
  const controlsContainerRef = useRef<HTMLDivElement | null>(null);

  // const [chartViewportHeight, setChartViewportHeight] = useState<number>(600);
  useEffect(() => {
    const HEADER_H = 56; // h-14 in header
    const MAIN_PADDING_V = 48; // p-6 top+bottom in <main>
    const GAPS = 16; // approximate vertical gaps between elements

    const recalc = () => {
      const controlsH = hideControls ? 0 : (controlsContainerRef.current?.offsetHeight || 0);
      const h = window.innerHeight - HEADER_H - MAIN_PADDING_V - GAPS - (hideControls ? 0 : GAPS) - 0;
      // Deduct controls height to fit without page scroll
      const finalH = Math.max(320, h - controlsH);
      setChartViewportHeight(finalH);
    };
    recalc();
    window.addEventListener('resize', recalc);
    return () => window.removeEventListener('resize', recalc);
  }, [hideControls]);

  // Load available symbols from stockList.ts
  useEffect(() => {
    let isMounted = true;
    const loadSymbols = async () => {
      try {
        await loadStockList();
        if (!isMounted) return;
        console.log('🔍 TechnicalAnalysis: Loading symbols from stockList.ts');
        const stocks = STOCK_LIST;
        console.log('✅ TechnicalAnalysis: Loaded symbols:', stocks.length, 'symbols');
        // Set default symbol if none selected and no current symbol
        if (!selectedStock && !symbol && stocks.length > 0) {
          // Try to set BBCA as default, fallback to first available
          const defaultSymbol = (stocks.includes('BBCA') ? 'BBCA' : stocks[0]) || 'BBCA';
          setSymbol(defaultSymbol);
        }
      } catch (error) {
        console.error('Error loading stock symbols:', error);
      }
    };

    loadSymbols();

    return () => {
      isMounted = false;
    };
  }, []);

  // Update symbol when selectedStock prop changes
  useEffect(() => {
    if (selectedStock && selectedStock !== symbol) {
      setSymbol(selectedStock);
    }
  }, [selectedStock, symbol]);

  // Fetch company name when symbol changes
  useEffect(() => {
    let isMounted = true;
    const fetchCompanyName = async () => {
      try {
        if (symbol === 'IDX') {
          setCompanyName('IDX Composite (IHSG)');
          return;
        }
        const rows = await fetchStockMetadata();
        if (!isMounted) return;
        const meta = rows.find(r => r.symbol === symbol);
        setCompanyName(meta?.name ? formatCompanyName(meta.name) : symbol);
      } catch (error) {
        console.error('Error fetching company name:', error);
        setCompanyName(symbol);
      }
    };

    if (symbol) {
      fetchCompanyName();
      // Save symbol to preferences
      menuPreferencesService.savePreferences(pageId, { symbol });
    }

    return () => {
      isMounted = false;
    };
  }, [symbol, pageId]);

  // Sync symbol with selectedStock prop if it changes (for Watchlist usage)
  useEffect(() => {
    if (selectedStock) {
      setSymbol(selectedStock);
    }
  }, [selectedStock]);

  // Stock search selector state for Technical Analysis control panel
  const [stockInput, setStockInput] = useState<string>(symbol || 'BBCA');
  const [showStockSuggestions, setShowStockSuggestions] = useState<boolean>(false);

  useEffect(() => {
    if (symbol) {
      setStockInput(symbol);
    }
  }, [symbol]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.stock-dropdown-container')) {
        setShowStockSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredStocks = useMemo(() => {
    if (!stockInput) return STOCK_LIST;
    return searchStocks(stockInput);
  }, [stockInput]);

  // Footprint chart settings
  const [footprintSettings, setFootprintSettings] = useState({
    showCrosshair: true,
    showPOC: true,
    showDelta: false, // Default delta off
    timeframe: '15m',
    zoom: 1.1 // Default zoom 1.1x
  });

  // Load saved colors from localStorage
  useEffect(() => {
    const savedColors = localStorage.getItem('chartColors');
    if (savedColors) {
      try {
        setChartColors(JSON.parse(savedColors));
      } catch (e) {
        console.log('Failed to load saved colors, using defaults');
      }
    }
  }, []);

  // Save colors to localStorage when they change
  useEffect(() => {
    localStorage.setItem('chartColors', JSON.stringify(chartColors));
  }, [chartColors]);

  // Save preferences to storage/cookies when they change
  useEffect(() => {
    menuPreferencesService.savePreferences(pageId, {
      indicatorChartHeight,
      chartViewportHeight,
      horizontalScale,
      verticalScale,
      rsiSettings,
      volumeHistogramSettings,
      indicators,
      timeframe,
      style,
    });
  }, [indicatorChartHeight, chartViewportHeight, horizontalScale, verticalScale, rsiSettings, volumeHistogramSettings, indicators, timeframe, style, pageId]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const menuContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const priceRef = useRef<any | null>(null);
  const volRef = useRef<any | null>(null);
  const indicatorRefs = useRef<{ [key: string]: any }>({});
  // Track any auxiliary series added for a single indicator (e.g., MACD signal, Stochastic %D)
  const indicatorAuxRefs = useRef<{ [key: string]: any[] }>({});
  // Unsubscribe for the Foreign/Domestic custom-legend crosshair tracker (re-subscribed each render)
  const foreignDomesticCrosshairUnsubRef = useRef<(() => void) | null>(null);

  // Separate chart refs for indicators
  const indicatorChartRefs = useRef<{ [key: string]: IChartApi }>({});
  const indicatorSeriesRefs = useRef<{ [key: string]: any }>({});
  const indicatorContainerRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const attachedVertPrimitivesRef = useRef<Array<{ series: any; primitive: VerticalLinePrimitive }>>([]);

  // Indicator management functions
  const addIndicator = (type: Indicator['type'], color: string, separateScale?: boolean, maMode: 'simple' | 'exponential' = 'simple') => {
    // Default periods for each indicator type
    const defaultPeriods = {
      'ma': 20,
      'sma': 20,
      'ema': 12,
      'rsi': 14,
      'macd': 12,
      'stochastic': 14,
      'volume_histogram': 1,
      'bazi_indicator': 1,
      'bazi_daily': 1,
      'bazi_monthly': 1,
      'buy_sell_frequency': 14,
      'foreign_domestic': 14,
      'daily_shio': 1,
      'daily_element': 1,
      'vertical_line_15d': 15,
    };

    const isSeparateType = type === 'rsi' || type === 'macd' || type === 'stochastic' || type === 'buy_sell_frequency';
    const effectiveSeparateScale = separateScale !== undefined ? separateScale : isSeparateType;

    let finalColor = color;
    if (type === 'vertical_line_15d') {
      try {
        const savedColor = localStorage.getItem('last_vertical_line_color');
        if (savedColor) {
          finalColor = savedColor;
        } else if (color) {
          localStorage.setItem('last_vertical_line_color', color);
        }
      } catch {}
    }

    const newIndicator: Indicator = {
      id: `${type}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      name: type === 'ma' ? `MA (${maMode === 'exponential' ? 'Exponential' : 'Simple'})` :
        type === 'stochastic' ? 'Stochastic' :
          type === 'buy_sell_frequency' ? 'Buy / Sell Frequency' :
            type === 'foreign_domestic' ? 'Foreign' :
              type === 'daily_shio' ? 'Daily Shio' :
                type === 'daily_element' ? 'Daily Element' :
                  type === 'bazi_daily' ? 'BaZi Daily' :
                    type === 'bazi_monthly' ? 'BaZi Monthly' :
                      type === 'bazi_indicator' ? 'BaZi Daily' :
                        type === 'vertical_line_15d' ? '15-day Cycle' :
                        `${type.toUpperCase()}`,
      type,
      period: (defaultPeriods as any)[type] || 1,
      color: finalColor,
      enabled: true,
      separateScale: effectiveSeparateScale,
      ...(type === 'ma' && { maMode }),
      ...(type === 'daily_shio' || type === 'daily_element' ? { dayLimit: 30 } : {}),
      ...(type === 'vertical_line_15d' ? { baseTime: null, intervalDays: 15, lineCount: 12, direction: 'forward', opacity: 0.85 } : {})
    };
    setIndicators(prev => [...prev, newIndicator]);

    if (type === 'vertical_line_15d') {
      setPlacingIndicatorId(newIndicator.id);
      setShowIndicatorSettings(false);
    }
  };

  const removeIndicator = (id: string) => {
    const indicator = indicators.find(ind => ind.id === id);

    setIndicators(prev => prev.filter(ind => ind.id !== id));

    // Purge worker results & readiness states from memory
    setComputedWorkerResults(prev => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
    setIndicatorSeriesReady(prev => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
    setIndicatorReloaded(prev => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });

    // Remove custom tooltips/legends
    if (indicator?.type === 'foreign_domestic') {
      try { foreignDomesticCrosshairUnsubRef.current?.(); } catch { }
      foreignDomesticCrosshairUnsubRef.current = null;
      if (foreignDomesticLegendRef.current) foreignDomesticLegendRef.current.style.display = 'none';
    }
    if (isBaZiType(indicator?.type)) {
      try { baziCrosshairUnsubRef.current[id]?.(); } catch { }
      delete baziCrosshairUnsubRef.current[id];
      if (baziTooltipRef.current) baziTooltipRef.current.style.display = 'none';
    }

    // Remove from main chart - clean up main series AND all aux series & primitives
    if (chartRef.current) {
      if (indicatorAuxRefs.current[id]) {
        indicatorAuxRefs.current[id].forEach(series => {
          try { chartRef.current?.removeSeries(series); } catch { }
          try { if (priceRef.current) priceRef.current.detachPrimitive(series); } catch { }
        });
        delete indicatorAuxRefs.current[id];
      }

      // Remove main series
      if (indicatorRefs.current[id]) {
        try { chartRef.current.removeSeries(indicatorRefs.current[id]); } catch { }
        delete indicatorRefs.current[id];
      }
    }

    // Remove separate chart if exists
    if (indicatorChartRefs.current[id]) {
      const chart = indicatorChartRefs.current[id];

      if ((chart as any)._timeScaleUnsubscribe) {
        try { (chart as any)._timeScaleUnsubscribe(); } catch { }
      }
      if ((chart as any)._crosshairUnsubscribe) {
        try { (chart as any)._crosshairUnsubscribe(); } catch { }
      }

      try { chart.remove(); } catch { }
      delete indicatorChartRefs.current[id];
    }

    // Clear container ref
    delete indicatorContainerRefs.current[id];
  };

  // Function to remove indicator from main chart when moved to separate chart
  const removeFromMainChart = (id: string) => {
    if (chartRef.current) {
      if (indicatorAuxRefs.current[id]) {
        indicatorAuxRefs.current[id].forEach(series => {
          try { chartRef.current?.removeSeries(series); } catch { }
          try { if (priceRef.current) priceRef.current.detachPrimitive(series); } catch { }
        });
        delete indicatorAuxRefs.current[id];
      }
      if (indicatorRefs.current[id]) {
        try { chartRef.current.removeSeries(indicatorRefs.current[id]); } catch { }
        delete indicatorRefs.current[id];
      }
    }
  };

  const toggleIndicator = (id: string) => {
    const indicator = indicators.find(ind => ind.id === id);
    if (!indicator) return;

    const newEnabled = !indicator.enabled;

    if (!newEnabled) {
      setComputedWorkerResults(prev => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
      setIndicatorSeriesReady(prev => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });

      if (indicator.type === 'foreign_domestic') {
        try { foreignDomesticCrosshairUnsubRef.current?.(); } catch { }
        foreignDomesticCrosshairUnsubRef.current = null;
        if (foreignDomesticLegendRef.current) foreignDomesticLegendRef.current.style.display = 'none';
      }
      if (isBaZiType(indicator.type)) {
        try { baziCrosshairUnsubRef.current[id]?.(); } catch { }
        delete baziCrosshairUnsubRef.current[id];
        if (baziTooltipRef.current) baziTooltipRef.current.style.display = 'none';
      }

      // Remove from main chart when disabled
      if (chartRef.current) {
        if (indicatorAuxRefs.current[id]) {
          indicatorAuxRefs.current[id].forEach(series => {
            try { chartRef.current?.removeSeries(series); } catch { }
            try { if (priceRef.current) priceRef.current.detachPrimitive(series); } catch { }
          });
          delete indicatorAuxRefs.current[id];
        }
        if (indicatorRefs.current[id]) {
          try { chartRef.current.removeSeries(indicatorRefs.current[id]); } catch { }
          delete indicatorRefs.current[id];
        }
      }

      // Remove separate chart when disabled
      if (indicatorChartRefs.current[id]) {
        const chart = indicatorChartRefs.current[id];
        if ((chart as any)._timeScaleUnsubscribe) {
          try { (chart as any)._timeScaleUnsubscribe(); } catch { }
        }
        if ((chart as any)._crosshairUnsubscribe) {
          try { (chart as any)._crosshairUnsubscribe(); } catch { }
        }
        try { chart.remove(); } catch { }
        delete indicatorChartRefs.current[id];
      }
    }

    setIndicators(prev => prev.map(ind =>
      ind.id === id ? { ...ind, enabled: newEnabled } : ind
    ));
  };

  const editIndicator = (indicator: Indicator) => {
    setEditingIndicator(indicator);
    setShowIndicatorEditor(true);
  };

  const updateIndicator = (updatedIndicator: Indicator) => {
    if (updatedIndicator.type === 'vertical_line_15d' && updatedIndicator.color) {
      try {
        localStorage.setItem('last_vertical_line_color', updatedIndicator.color);
      } catch {}
    }

    // Check if indicator is being moved from main chart to separate chart
    const currentIndicator = indicators.find(ind => ind.id === updatedIndicator.id);
    if (currentIndicator && !currentIndicator.separateScale && updatedIndicator.separateScale) {
      // Remove from main chart when moving to separate chart
      removeFromMainChart(updatedIndicator.id);
    }

    setIndicators(prev => prev.map(ind =>
      ind.id === updatedIndicator.id ? updatedIndicator : ind
    ));
    setShowIndicatorEditor(false);
    setEditingIndicator(null);
  };

  // Function to sync time scale with all indicator charts
  const syncTimeScaleWithIndicators = () => {
    if (!chartRef.current || isSyncingTimeScaleRef.current) return;

    const mainTimeScale = chartRef.current.timeScale();
    const logicalRange = mainTimeScale.getVisibleLogicalRange();

    if (logicalRange) {
      isSyncingTimeScaleRef.current = true;
      try {
        Object.values(indicatorChartRefs.current).forEach(indicatorChart => {
          if (indicatorChart && (indicatorChart as any).timeScale) {
            (indicatorChart as any).timeScale().setVisibleLogicalRange(logicalRange);
          }
        });
      } finally {
        isSyncingTimeScaleRef.current = false;
      }
    }
  };

  // Get theme-aware colors
  const getThemeColors = () => {
    const isDark = document.documentElement.classList.contains('dark');
    return {
      textColor: isDark ? '#f9fafb' : '#111827',
      gridColor: isDark ? '#4b5563' : '#e5e7eb',
      borderColor: isDark ? '#6b7280' : '#d1d5db',
      axisTextColor: isDark ? '#d1d5db' : '#6b7280'
    };
  };

  // symbols filter logic removed

  // Removed useEffect that was forcing searchQuery to match symbol
  // This was causing the clear button to not work properly

  // Handle keyboard navigation (like MarketRotationRRC)
  // Keyboard navigation handler removed

  // Load stock data from API when symbol changes
  useEffect(() => {
    if (isActive === false) return;
    let cancelled = false;

    // Serve straight from cache if we already loaded this symbol today —
    // skips the loading overlay entirely so the chart shows up instantly.
    const formattedStock = symbol.trim().toUpperCase();
    const cachedEntry = ohlcChartCache.get(formattedStock);
    const isCacheInvalid = cachedEntry && cachedEntry.rows.length > 0 && cachedEntry.rows[0].open === 0 && cachedEntry.rows[0].close > 0;
    if (cachedEntry && cachedEntry.cachedDate === localDateKey() && !isCacheInvalid) {
      console.log(`📊 Using cached OHLC rows for ${symbol} (${cachedEntry.rows.length} rows)`);
      setErr(null);
      setPlotted(0);
      setIndicatorSeriesReady({});
      setIndicatorReloaded({});
      setRows(cachedEntry.rows);
      setSrc('file');
      setIsLoadingData(false);
      return () => { cancelled = true; };
    }

    // Reset state for new symbol
    setIsLoadingData(true);
    setErr(null);
    setPlotted(0);
    setIndicatorSeriesReady({});
    setIndicatorReloaded({});
    setRows([]);

    (async () => {
      try {


        // Load OHLC from blob storage (SAS token + parquet), same method as OrderbookReplay
        console.log(`Loading stock data for ${symbol} from blob storage...`);
        const tokenRes = await api.getOrderbookReplaySasToken();
        if (cancelled) return;
        if (!tokenRes.success || !tokenRes.data) {
          throw new Error(tokenRes.error || 'Failed to authorize access.');
        }
        const { containerUrl, sasToken } = tokenRes.data;

        let ohlcPath = '';
        if (formattedStock === 'IDX') {
          ohlcPath = 'ohlc/index/COMPOSITE.parquet';
        } else {
          // Resolve the OHLC blob path client-side from stock metadata (symbol -> sector),
          // avoiding the /api/orderbook-replay/ohlc-path backend lookup entirely.
          const metadata = await fetchStockMetadata();
          if (cancelled) return;
          const stockMeta = metadata.find(m => m.symbol === formattedStock);
          if (!stockMeta?.sector) {
            throw new Error(`No data found for ${symbol}`);
          }
          ohlcPath = `ohlc/stocks/${stockMeta.sector}/${formattedStock}.parquet`;
        }

        const ohlcRows = await loadOhlcByPath(ohlcPath, containerUrl, sasToken);
        if (cancelled) return;
        if (!ohlcRows.length) {
          throw new Error(`No data found for ${symbol}`);
        }

        // Convert blob data to OhlcRow format
        const parsed: OhlcRow[] = ohlcRows.map((row: any) => {
          // Convert date to Unix timestamp
          const dateStr = row.Date || row.date || '';
          const time = new Date(dateStr).getTime() / 1000;

          const closeVal = parseFloat(row.Close ?? row.close ?? 0);
          
          // Index files (e.g. COMPOSITE.parquet) store 'Previous' instead of 'Open'
          const rawOpen = row.Open ?? row.open ?? row.Previous ?? row.previous ?? row.Prev ?? row.prev;
          const openParsed = parseFloat(rawOpen);
          const openVal = (!isNaN(openParsed) && openParsed > 0) ? openParsed : closeVal;

          const rawHigh = parseFloat(row.High ?? row.high ?? 0);
          const rawLow = parseFloat(row.Low ?? row.low ?? 0);

          const highVal = rawHigh > 0 ? rawHigh : Math.max(openVal, closeVal);
          const lowVal = rawLow > 0 ? rawLow : Math.min(openVal, closeVal);

          return {
            time: Math.floor(time),
            open: openVal,
            high: highVal,
            low: lowVal,
            close: closeVal,
            volume: parseFloat(row.Volume ?? row.volume ?? 0)
          };
        }).filter((row: OhlcRow) => row.time > 0); // Filter out invalid dates

        console.log(`📊 Loaded ${parsed.length} rows for ${symbol} from blob storage`);
        console.log(`📊 First 3 rows:`, parsed.slice(0, 3));
        console.log(`📊 Last 3 rows:`, parsed.slice(-3));
        if (!parsed.length) throw new Error(`No valid OHLC data for ${symbol}`);

        ohlcChartCache.set(formattedStock, { cachedDate: localDateKey(), rows: parsed });

        if (!cancelled) {
          console.log(`✅ Data loaded successfully for ${symbol}, setting rows and stopping loading`);
          setRows(parsed);
          setSrc('file');
        }
      } catch (e: any) {
        if (!cancelled) {
          console.error(`Failed to load data for ${symbol}:`, e?.message);
          setErr(e?.message ?? 'API load error');
          setRows([]);
          setSrc('none');
        }
      } finally {
        if (!cancelled) {
          console.log(`🔄 Setting isLoadingData to false for ${symbol}`);
          setIsLoadingData(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [symbol, isActive]);

  // Load bid/ask data from API when symbol changes and Buy/Sell Frequency indicator is enabled
  useEffect(() => {
    if (isActive === false) return;
    let cancelled = false;
    (async () => {
      try {
        // Only fetch bid/ask data if the Buy/Sell Frequency indicator is active/enabled
        const hasBuySellFrequency = indicators.some(ind => ind.enabled && ind.type === 'buy_sell_frequency');
        if (!hasBuySellFrequency) {
          if (!cancelled) {
            setBidAskData([]);
          }
          return;
        }

        // Get all available dates from OHLC data
        if (rows.length === 0) {
          console.log('📊 No OHLC data available yet, skipping bid/ask data load');
          return;
        }

        console.log(`📊 Loading Buy/Sell Frequency data for ${symbol} across all available dates...`);

        // Extract unique dates from OHLC data
        const uniqueDates = new Set<string>();
        rows.forEach(row => {
          const date = new Date(row.time * 1000);
          const dateStr = date.toISOString().split('T')[0]?.replace(/-/g, '') || '';
          if (dateStr) {
            uniqueDates.add(dateStr);
          }
        });

        console.log(`📊 Found ${uniqueDates.size} unique dates in OHLC data:`, Array.from(uniqueDates).sort());

        // Generate date range starting from today going backwards
        const today = new Date();
        const oneYearAgo = new Date(today);
        oneYearAgo.setFullYear(today.getFullYear() - 1);

        console.log(`📊 Generating date range from ${oneYearAgo.toISOString().split('T')[0]} to ${today.toISOString().split('T')[0]}`);

        // Generate all possible dates in the last year
        const possibleDates: string[] = [];
        const currentDate = new Date(today);

        while (currentDate >= oneYearAgo) {
          const isoString = currentDate.toISOString();
          const dateStr = isoString.split('T')[0]?.replace(/-/g, '') || '';
          if (dateStr) {
            possibleDates.push(dateStr);
          }
          currentDate.setDate(currentDate.getDate() - 1);
        }

        console.log(`📊 Generated ${possibleDates.length} possible dates in the last year`);

        // Filter to only include dates that exist in OHLC data
        const availableDates = possibleDates.filter(dateStr => uniqueDates.has(dateStr));
        console.log(`📊 Found ${availableDates.length} available dates in OHLC data:`, availableDates.slice(0, 10), '...');

        // Load bid/ask data starting from the most recent date going backwards
        const allBidAskData: any[] = [];
        const maxDates = 365; // 1 year limit
        const dateArray = availableDates.slice(0, maxDates);

        console.log(`📊 Loading bid/ask data for ${dateArray.length} dates (limited to ${maxDates} most recent dates)`);

        let dataFoundCount = 0;
        const maxDataDays = 21; // Stop after finding 21 days of data

        for (const dateStr of dateArray) {
          // Check if cancelled before each API call
          if (cancelled) {
            console.log(`📊 Bid/ask data loading cancelled for ${symbol}`);
            return;
          }

          try {
            console.log(`📊 Loading bid/ask data for ${symbol} on ${dateStr}...`);
            // Use the same API endpoint as FootprintChart for consistency
            const result = await api.getBidAskData(symbol, dateStr);

            // Check if cancelled after API call
            if (cancelled) {
              console.log(`📊 Bid/ask data loading cancelled for ${symbol} after API call`);
              return;
            }

            if (result.success && result.data?.data && result.data.data.length > 0) {
              console.log(`✅ Buy/Sell Frequency data found for ${symbol} on ${dateStr}: ${result.data.data.length} records`);

              // Add date field to each record (same as FootprintChart)
              const dataWithDate = result.data.data.map((record: any) => ({
                ...record,
                date: dateStr
              }));

              for (let i = 0; i < dataWithDate.length; i++) {
                allBidAskData.push(dataWithDate[i]);
              }
              dataFoundCount++;

              // Stop if we have enough data days (21 days)
              if (dataFoundCount >= maxDataDays) {
                console.log(`📊 Found data for ${dataFoundCount} days, stopping search`);
                break;
              }
            } else {
              console.log(`⚠️ No bid/ask data for ${symbol} on ${dateStr} - skipping`);
            }
          } catch (error) {
            console.warn(`⚠️ Error loading bid/ask data for ${symbol} on ${dateStr}:`, error);
          }
        }

        console.log(`📊 Total bid/ask data loaded: ${allBidAskData.length} records across ${dataFoundCount} days with data`);
        if (!cancelled) {
          setBidAskData(allBidAskData);
        }

      } catch (e: any) {
        if (!cancelled) {
          console.error(`Failed to load bid/ask data for ${symbol}:`, e?.message);
          setBidAskData([]);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [symbol, rows, indicators, isActive]); // Add rows and indicators dependency to reload when settings change

  // Load foreign flow data (for the Foreign/Domestic indicator) when symbol changes
  useEffect(() => {
    if (isActive === false) return;
    let cancelled = false;
    (async () => {
      if (rows.length === 0) {
        return;
      }
      try {
        const result = await fetchForeignFlowDataDirect(symbol, 1000);
        if (cancelled) return;
        if (result.success && Array.isArray(result.data?.data)) {
          setForeignFlowData(result.data.data);
        } else {
          setForeignFlowData([]);
        }
      } catch (e: any) {
        if (!cancelled) {
          console.warn(`Failed to load foreign flow data for ${symbol}:`, e?.message);
          setForeignFlowData([]);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [symbol, rows, isActive]);

  // Detect data frequency and available timeframes
  const dataFrequency = useMemo(() => {
    if (!rows.length) return 'unknown';

    const sortedRows = [...rows].sort((a, b) => a.time - b.time);
    if (sortedRows.length < 2) return 'unknown';

    // Calculate time differences between consecutive data points
    const timeDiffs: number[] = [];
    for (let i = 1; i < Math.min(sortedRows.length, 10); i++) {
      const diff = (sortedRows[i]?.time ?? 0) - (sortedRows[i - 1]?.time ?? 0);
      timeDiffs.push(diff);
    }

    const avgDiff = timeDiffs.reduce((sum, diff) => sum + diff, 0) / timeDiffs.length;
    const avgDiffMinutes = avgDiff / 60;

    console.log('Data frequency analysis:', {
      avgDiffSeconds: avgDiff,
      avgDiffMinutes: avgDiffMinutes,
      timeDiffs: timeDiffs.slice(0, 5)
    });

    if (avgDiffMinutes <= 1) return '1min';
    if (avgDiffMinutes <= 5) return '5min';
    if (avgDiffMinutes <= 15) return '15min';
    if (avgDiffMinutes <= 30) return '30min';
    if (avgDiffMinutes <= 60) return '1hour';
    if (avgDiffMinutes <= 1440) return '1day';
    return 'unknown';
  }, [rows]);

  // Get available timeframes based on data frequency
  const availableTimeframes = useMemo(() => {
    const allTimeframes: { value: Timeframe; label: string; minFreq: string }[] = [
      { value: '1H', label: '1 Hour', minFreq: '1hour' },
      { value: '1D', label: '1 Day', minFreq: '1day' },
      { value: '1W', label: '1 Week', minFreq: '1day' },
      { value: '1MO', label: '1 Month', minFreq: '1day' },
      { value: '2W_BAZI', label: 'Bi-Weekly (BaZi)', minFreq: '1day' },
      { value: '1M_BAZI', label: 'Monthly (BaZi)', minFreq: '1day' },
      { value: '3M', label: '3 Months', minFreq: '1day' },
      { value: '6M', label: '6 Months', minFreq: '1day' },
      { value: '1Y', label: '1 Year', minFreq: '1day' }
    ];

    const freqOrder = ['1min', '5min', '15min', '30min', '1hour', '1day', 'unknown'];
    const dataFreqIndex = freqOrder.indexOf(dataFrequency);

    return allTimeframes.filter(tf => {
      const tfFreqIndex = freqOrder.indexOf(tf.minFreq);
      return tfFreqIndex <= dataFreqIndex;
    });
  }, [dataFrequency]);

  // Auto-select appropriate timeframe when data changes
  useEffect(() => {
    if (availableTimeframes.length > 0) {
      const currentTimeframe = availableTimeframes.find(tf => tf.value === timeframe);
      if (!currentTimeframe) {
        // Current timeframe is not available, select the first available one
        setTimeframe(availableTimeframes[0]?.value ?? '1D');
      }
    }
  }, [availableTimeframes, timeframe]);

  // Aggregate data based on timeframe
  const filteredRows = useMemo(() => {
    if (!rows.length) return rows;

    // Sort rows by time
    const sortedRows = [...rows].sort((a, b) => a.time - b.time);

    console.log('📊 Aggregating data for timeframe:', timeframe, 'Total rows:', rows.length);
    console.log('📊 First 3 original rows:', rows.slice(0, 3));
    console.log('📊 Last 3 original rows:', rows.slice(-3));

    // Handle minute-based timeframes
    if (timeframe === '1M') {
      // 1 minute - no aggregation needed
      return sortedRows;
    }

    if (timeframe === '5M') {
      // Group by 5-minute intervals
      return aggregateByMinute(sortedRows, 5);
    }

    if (timeframe === '15M') {
      // Group by 15-minute intervals
      return aggregateByMinute(sortedRows, 15);
    }

    if (timeframe === '30M') {
      // Group by 30-minute intervals
      return aggregateByMinute(sortedRows, 30);
    }

    if (timeframe === '1H') {
      // Group by hour
      return aggregateByHour(sortedRows);
    }

    if (timeframe === '1D') {
      // For 1D, group by trading day (7 AM to 1 AM next day)
      return aggregateByTradingDay(sortedRows);
    }

    if (timeframe === '1W') {
      // Group by week (Monday to Friday = 1 bar)
      return aggregateByWeek(sortedRows);
    }

    if (timeframe === '1MO') {
      // Group by month (each month = 1 bar)
      return aggregateByMonth(sortedRows);
    }

    if (timeframe === '3M') {
      // Group by quarter (every 3 months = 1 bar)
      return aggregateByQuarter(sortedRows);
    }

    if (timeframe === '6M') {
      // Group by half-year (every 6 months = 1 bar)
      return aggregateByHalfYear(sortedRows);
    }

    if (timeframe === '2W_BAZI') {
      return aggregateByBaZiBiWeek(sortedRows);
    }

    if (timeframe === '1M_BAZI') {
      return aggregateByBaZiMonth(sortedRows);
    }

    if (timeframe === '1Y') {
      // Group by year (every year = 1 bar)
      return aggregateByYear(sortedRows);
    }

    console.log('📊 Final filtered rows:', sortedRows.length);
    console.log('📊 First 3 filtered rows:', sortedRows.slice(0, 3));
    console.log('📊 Last 3 filtered rows:', sortedRows.slice(-3));
    return sortedRows;
  }, [rows, timeframe]);

  const getVertLineItemsForIndicator = useCallback((ind: Indicator): VerticalLineItem[] => {
    if (!ind || !ind.enabled || ind.hidden || ind.type !== 'vertical_line_15d' || ind.baseTime == null) {
      return [];
    }
    if (placingIndicatorId != null && ind.id === placingIndicatorId) {
      return [];
    }
    const lineDates = calculateVerticalLineDates(
      ind.baseTime,
      ind.intervalDays || 15,
      ind.lineCount || 12,
      filteredRows,
      ind.direction || 'forward'
    );
    return lineDates.map(item => ({
      time: item.timestampSec,
      label: item.label,
      isT0: item.index === 0,
      color: ind.color || '#8b5cf6',
      opacity: ind.opacity ?? 0.85,
      index: item.index,
    }));
  }, [placingIndicatorId, filteredRows]);

  // Note: Preview primitives are centrally managed by line 3168 useEffect along with settled vertical lines

  const setPreviewItems = (items: VerticalLineItem[]) => {
    if (previewPrimitiveRef.current) {
      previewPrimitiveRef.current.setData(items, filteredRows);
    }
    subPanePreviewPrimitivesRef.current.forEach(p => {
      try { p.setData(items, filteredRows); } catch {}
    });
  };

  // Offload indicator calculations to Web Worker off main thread
  useEffect(() => {
    if (isActive === false || filteredRows.length === 0) return;
    let isCancelled = false;

    // Purge stale worker results from previous timeframe immediately
    setComputedWorkerResults({});

    setIsWorkerCalculating(true);
    indicatorWorkerManager.computeIndicators({
      filteredRows,
      bidAskData,
      foreignFlowData,
      timeframe,
      indicators
    }).then(results => {
      if (!isCancelled) {
        setComputedWorkerResults(results);
        setIsWorkerCalculating(false);
      }
    }).catch(err => {
      if (!isCancelled) {
        console.warn('Worker computation failed:', err);
        setIsWorkerCalculating(false);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [filteredRows, bidAskData, foreignFlowData, indicators, timeframe, isActive]);

  // Create chart when container is ready and data is loaded
  useEffect(() => {
    // Early return for footprint style - don't create any chart
    if (style === 'footprint' as ChartStyle) {
      console.log('🚫 Skipping chart creation for footprint style');
      // Cleanup any existing chart
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
      return;
    }

    if (isActive === false) return;

    // Don't create chart if still loading or no data
    if (isLoadingData || rows.length === 0) {
      console.log('⏳ Skipping chart creation - loading or no data');
      return;
    }

    const el = containerRef.current;
    if (!el) {
      console.log('⏳ Skipping chart creation - container not ready');
      return;
    }

    // Cleanup existing chart when style changes or data changes
    if (chartRef.current) {
      console.log('🗑️ Removing existing chart for recreation');
      try {
        const currentLogicalRange = chartRef.current.timeScale().getVisibleLogicalRange();
        if (currentLogicalRange) {
          savedLogicalRangeRef.current = currentLogicalRange;
        }
      } catch (e) {
        console.warn('Could not save visible logical range:', e);
      }
      chartRef.current.remove();
      chartRef.current = null;
      priceRef.current = null;
      volRef.current = null;
    }

    console.log('📊 Creating lightweight-charts for style:', style, 'with', rows.length, 'rows');

    const width = el.clientWidth || 800;
    const height = el.clientHeight || 420;
    const colors = getThemeColors();
    console.log(`📊 Creating chart with dimensions: ${width}x${height}`);
    console.log(`📊 Container element:`, el);
    chartRef.current = createChart(el, {
      width,
      height,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: colors.axisTextColor
      },
      grid: {
        horzLines: { visible: false },
        vertLines: { visible: false }
      },
      rightPriceScale: {
        borderColor: colors.borderColor,
        scaleMargins: {
          top: 0.02,
          bottom: 0.05,
        }
      },
      leftPriceScale: {
        borderColor: colors.borderColor,
        scaleMargins: {
          top: 0.02,
          bottom: 0.05,
        }
      },
      timeScale: {
        borderColor: colors.borderColor,
        visible: !indicators.some(ind => ind.enabled && isIndicatorSeparate(ind))
      },
      crosshair: { mode: style === 'footprint' as ChartStyle ? CrosshairMode.Hidden : CrosshairMode.Normal },
      handleScroll: style === 'footprint' as ChartStyle ? false : true,
      handleScale: style === 'footprint' as ChartStyle ? false : true,
    });
    // Reset indicator refs when recreating chart to avoid stale series handles
    indicatorRefs.current = {};
    indicatorAuxRefs.current = {};
    vertLinePrimitivesRef.current = {};
  }, [style, isLoadingData, rows.length, isActive]);

  // Update chart data when data changes
  useEffect(() => {
    if (isActive === false) return;
    if (!chartRef.current || style === 'footprint' as ChartStyle || isLoadingData || rows.length === 0) return;

    console.log('📊 Chart data useEffect triggered:', {
      filteredRowsLength: filteredRows.length,
      bidAskDataLength: bidAskData.length,
      indicatorsCount: indicators.length,
      enabledIndicatorsCount: indicators.filter(ind => ind.enabled).length,
      isLoadingData
    });

    const chart = chartRef.current!;
    const activeLogicalRange = chart.timeScale().getVisibleLogicalRange() || savedLogicalRangeRef.current;
    const colors = getThemeColors();

    // Update timeScale visibility based on indicators
    const hasVisibleSeparateCharts = indicators.some(ind => ind.enabled && isIndicatorSeparate(ind));
    console.log(`📊 TimeScale visibility: ${!hasVisibleSeparateCharts}, hasVisibleSeparateCharts: ${hasVisibleSeparateCharts}`);
    chart.timeScale().applyOptions({
      visible: !hasVisibleSeparateCharts
    });

    // Show the left price scale only when Foreign/Domestic is active — its values
    // (net buy volume) live on a different scale than the candle prices on the right.
    const hasForeignDomestic = indicators.some(ind => ind.enabled && !isIndicatorSeparate(ind) && ind.type === 'foreign_domestic');
    chart.applyOptions({
      leftPriceScale: {
        visible: hasForeignDomestic,
        borderColor: colors.borderColor,
        scaleMargins: {
          top: 0.02,
          bottom: 0.05,
        }
      },
      rightPriceScale: {
        borderColor: colors.borderColor,
        scaleMargins: {
          top: 0.02,
          bottom: 0.05,
        }
      }
    });

    // bersihkan seri lama
    if (priceRef.current) {
      try {
        chart.removeSeries(priceRef.current);
      } catch (error) {
        console.warn('Error removing price series:', error);
      } finally {
        priceRef.current = null;
      }
    }
    if (volRef.current) {
      try {
        chart.removeSeries(volRef.current);
      } catch (error) {
        console.warn('Error removing volume series:', error);
      } finally {
        volRef.current = null;
      }
    }

    if (!filteredRows.length) {
      console.log('No rows to plot');
      setPlotted(0);
      return;
    }

    console.log(`📊 Rendering chart with ${filteredRows.length} rows, style: ${style}, isLoadingData: ${isLoadingData}`);
    console.log(`📊 First 3 rows:`, filteredRows.slice(0, 3));
    console.log(`📊 Last 3 rows:`, filteredRows.slice(-3));
    try {
      // --- price series (v5 pakai addSeries(TipeSeri, opsi)) ---
      if (style === 'line') {
        const s = chart.addSeries(LineSeries, {
          color: chartColors.area.line,
          lineWidth: 2,
        });
        const lineData = filteredRows.map(d => ({ time: d.time as any, value: d.close }));
        console.log(`📊 Setting line data: ${lineData.length} points`);
        s.setData(lineData);
        priceRef.current = s;
      } else if (style === 'footprint' as ChartStyle) {
        // For footprint chart, we'll use a simple line series as base
        const s = chart.addSeries(LineSeries, {
          color: '#666666',
          lineWidth: 1,
          crosshairMarkerVisible: false,
          lastValueVisible: false,
          priceLineVisible: false,
        });

        // Set minimal line data for chart structure
        const lineData = filteredRows.map(d => ({
          time: d.time as any,
          value: (d.open + d.close) / 2, // Use mid price
        }));
        s.setData(lineData);
        priceRef.current = s;
      } else {
        const s = chart.addSeries(CandlestickSeries, {
          upColor: chartColors.candles.up,
          downColor: chartColors.candles.down,
          borderVisible: false,
          wickUpColor: chartColors.candles.wickUp,
          wickDownColor: chartColors.candles.wickDown,
        });
        const candleData = filteredRows.map(d => ({
          time: d.time as any,
          open: d.open,
          high: d.high,
          low: d.low,
          close: d.close,
        }));
        console.log(`📊 Setting candle data: ${candleData.length} candles`);
        s.setData(candleData);
        priceRef.current = s;
      }

      // Volume histogram removed - now handled as separate indicator

      // --- indicators ---
      console.log('📊 Processing indicators:', {
        totalIndicators: indicators.length,
        enabledIndicators: indicators.filter(ind => ind.enabled).length,
        bidAskDataAvailable: bidAskData.length > 0
      });

      // Collect all markers for Daily Shio and Element first
      const allMarkers: SeriesMarker<any>[] = [];

      indicators.forEach(indicator => {
        console.log(`📊 Processing indicator: ${indicator.type} (${indicator.id})`, {
          enabled: indicator.enabled,
          separateScale: indicator.separateScale,
          period: indicator.period
        });

        // Remove existing series if indicator is disabled or hidden
        if (!indicator.enabled || indicator.hidden) {
          if (indicator.type === 'foreign_domestic') {
            try { foreignDomesticCrosshairUnsubRef.current?.(); } catch { }
            foreignDomesticCrosshairUnsubRef.current = null;
            if (foreignDomesticLegendRef.current) foreignDomesticLegendRef.current.style.display = 'none';
          }
          if (isBaZiType(indicator.type)) {
            try { baziCrosshairUnsubRef.current[indicator.id]?.(); } catch { }
            delete baziCrosshairUnsubRef.current[indicator.id];
            if (baziTooltipRef.current) baziTooltipRef.current.style.display = 'none';
          }
          if (chartRef.current) {
            try {
              if (indicatorRefs.current[indicator.id]) {
                chartRef.current?.removeSeries(indicatorRefs.current[indicator.id]);
              }
            } catch { }
          }
          delete indicatorRefs.current[indicator.id];
          // Remove any auxiliary series for this indicator
          if (indicatorAuxRefs.current[indicator.id]) {
            try {
              indicatorAuxRefs.current[indicator.id]?.forEach(s => {
                try { if (chartRef.current) chartRef.current.removeSeries(s); } catch { }
                try { if (priceRef.current) priceRef.current.detachPrimitive(s); } catch { }
              });
            } catch { }
          }
          delete indicatorAuxRefs.current[indicator.id];
          return;
        }

        const activeVertLineInds = indicators.filter(ind => ind.enabled && !ind.hidden && ind.type === 'vertical_line_15d' && ind.baseTime != null);

        if (indicator.type === 'vertical_line_15d') {
          return;
        }

        // Skip separate scale indicators for main chart
        if (isIndicatorSeparate(indicator)) return;

        let indicatorData: IndicatorData[] = [];
        const workerRes = computedWorkerResults[indicator.id];

        switch (indicator.type) {
          case 'ma':
            if (workerRes?.data) {
              indicatorData = workerRes.data;
            } else if ((indicator.maMode || 'simple') === 'exponential') {
              indicatorData = calculateEMA(filteredRows, indicator.period);
            } else {
              indicatorData = calculateSMA(filteredRows, indicator.period);
            }
            break;
          case 'sma':
            indicatorData = workerRes?.data || calculateSMA(filteredRows, indicator.period);
            break;
          case 'ema':
            indicatorData = workerRes?.data || calculateEMA(filteredRows, indicator.period);
            break;
          case 'rsi':
            indicatorData = workerRes?.data || calculateRSI(filteredRows, indicator.period);
            break;
          case 'macd':
            indicatorData = workerRes?.macd || calculateMACD(filteredRows, 12, 26, 9).macd;
            break;
          case 'stochastic':
            indicatorData = workerRes?.k || calculateStochastic(filteredRows, indicator.period, 3).k;
            break;
          case 'volume_histogram':
            indicatorData = workerRes?.data || calculateVolumeHistogram(filteredRows);
            break;
          case 'bazi_indicator':
          case 'bazi_daily':
          case 'bazi_monthly': {
            const baziMode = indicator.type === 'bazi_monthly' ? 'monthly' : 'daily';
            indicatorData = workerRes?.data || calculateBaZiIndicator(filteredRows, timeframe, baziMode).data;
            break;
          }
          case 'buy_sell_frequency':
            indicatorData = workerRes?.buyFreq || calculateBuySellFrequency(filteredRows, bidAskData, indicator.period).buyFreq;
            break;
          case 'foreign_domestic':
            indicatorData = workerRes?.data || calculateForeignDomestic(filteredRows, foreignFlowData);
            break;
          case 'daily_shio':
            if (workerRes?.data) {
              indicatorData = workerRes.data;
              (indicator as any).labels = workerRes.labels;
              (indicator as any).colors = workerRes.colors;
            } else {
              const shioResult = calculateDailyShio(filteredRows, timeframe);
              indicatorData = shioResult.data;
              (indicator as any).labels = shioResult.labels;
              (indicator as any).colors = shioResult.colors;
            }
            break;
          case 'daily_element':
            if (workerRes?.data) {
              indicatorData = workerRes.data;
              (indicator as any).labels = workerRes.labels;
              (indicator as any).colors = workerRes.colors;
            } else {
              const elementResult = calculateDailyElement(filteredRows, timeframe);
              indicatorData = elementResult.data;
              (indicator as any).labels = elementResult.labels;
              (indicator as any).colors = elementResult.colors;
            }
            break;
        }

        console.log(`📊 Indicator ${indicator.type} data check:`, {
          indicatorId: indicator.id,
          dataLength: indicatorData.length,
          hasData: indicatorData.length > 0,
          sampleData: indicatorData.slice(0, 3)
        });

        if (indicatorData.length > 0) {
          console.log(`📊 Adding ${indicator.type} series to main chart:`, {
            indicatorId: indicator.id,
            dataLength: indicatorData.length,
            sampleData: indicatorData.slice(0, 3),
            chartExists: !!chartRef.current
          });

          // Remove existing series (and any aux) if they exist
          if (chartRef.current) {
            try {
              if (indicatorRefs.current[indicator.id]) {
                chartRef.current?.removeSeries(indicatorRefs.current[indicator.id]);
              }
            } catch { }
            if (indicatorAuxRefs.current[indicator.id]) {
              indicatorAuxRefs.current[indicator.id]?.forEach(s => {
                try { chartRef.current?.removeSeries(s); } catch { }
              });
              indicatorAuxRefs.current[indicator.id] = [];
            }
          }

          // Add to main chart
          let indicatorSeries: any;
          if (isPaneIndicator(indicator)) {
            // Find pane index for this indicator among all active pane-based indicators on the main chart
            const paneIndicators = indicators.filter(ind => ind.enabled && !ind.hidden && isPaneIndicator(ind));
            const paneIdx = paneIndicators.findIndex(ind => ind.id === indicator.id);
            const targetPaneIndex = 1 + (paneIdx >= 0 ? paneIdx : 0);

            if (isBaZiType(indicator.type)) {
              const baziMode = indicator.type === 'bazi_monthly' ? 'monthly' : 'daily';
              const baziCalc = workerRes?.mainElementData ? workerRes : calculateBaZiIndicator(filteredRows, timeframe, baziMode);
              const { mainElementData, shioElementData, textItems } = baziCalc;

              // Layer 1 (Element Utama: height 1.0)
              indicatorSeries = chart.addSeries(HistogramSeries, {
                priceFormat: { type: 'volume' },
                priceScaleId: 'right',
                color: indicator.color,
                lastValueVisible: false,
                priceLineVisible: false,
                autoscaleInfoProvider: () => ({
                  priceRange: { minValue: 0, maxValue: 1.0 },
                  margins: { above: 0, below: 0 },
                }),
              }, targetPaneIndex);
              indicatorSeries.priceScale().applyOptions({
                scaleMargins: { top: 0, bottom: 0 },
                visible: true,
                borderColor: colors.borderColor,
              });
              indicatorSeries.setData(mainElementData as any);

              // Layer 2 (Element Shio: height 0.5) - stacked on top of Layer 1 (0 to 0.5)
              const shioSeries = chart.addSeries(HistogramSeries, {
                priceFormat: { type: 'volume' },
                priceScaleId: 'right',
                color: indicator.color,
                lastValueVisible: false,
                priceLineVisible: false,
                autoscaleInfoProvider: () => ({
                  priceRange: { minValue: 0, maxValue: 1.0 },
                  margins: { above: 0, below: 0 },
                }),
              }, targetPaneIndex);
              shioSeries.priceScale().applyOptions({
                scaleMargins: { top: 0, bottom: 0 },
                visible: true,
                borderColor: colors.borderColor,
              });
              shioSeries.setData(shioElementData as any);

              // Attach BaZi text primitive to render 'A' and 'I' inside upper and lower boxes
              const baziTextPrimitive = new BaZiTextPrimitive(textItems);
              shioSeries.attachPrimitive(baziTextPrimitive);

              // Set series ref for central vertical line primitive management
              indicatorSeriesRefs.current[indicator.id] = shioSeries;

              if (!indicatorAuxRefs.current[indicator.id]) {
                indicatorAuxRefs.current[indicator.id] = [];
              }
              indicatorAuxRefs.current[indicator.id]?.push(shioSeries);

              // Subscribe crosshair move for BaZi tooltip (simple text on touch > 1 second)
              try { baziCrosshairUnsubRef.current?.(); } catch { }
              let baziHoverTimer: any = null;
              const baziMainSeries = indicatorSeries;
              const handleBaziCrosshairMove = (param: any) => {
                const el = baziTooltipRef.current;
                if (baziHoverTimer) {
                  clearTimeout(baziHoverTimer);
                  baziHoverTimer = null;
                }
                if (!el) return;

                if (placingIndicatorIdRef.current || !param || param.point === undefined || !param.time) {
                  if (baziHoverTimer) clearTimeout(baziHoverTimer);
                  el.style.display = 'none';
                  return;
                }

                // Check if cursor is inside BaZi pane using coordinateToPrice
                let priceAtY: number | null = null;
                try {
                  priceAtY = baziMainSeries.coordinateToPrice(param.point.y);
                } catch { }

                // If cursor is outside the BaZi indicator pane, hide tooltip
                if (priceAtY === null || priceAtY === undefined || priceAtY < 0 || priceAtY > 1.05) {
                  el.style.display = 'none';
                  return;
                }

                let date: Date | null = null;
                if (typeof param.time === 'number') {
                  date = new Date(param.time * 1000);
                } else if (typeof param.time === 'string') {
                  date = new Date(param.time);
                } else if (param.time && typeof param.time === 'object') {
                  const t = param.time as any;
                  date = new Date(t.year, t.month - 1, t.day);
                }

                if (!date || isNaN(date.getTime())) {
                  el.style.display = 'none';
                  return;
                }

                const baziMode = indicator.type === 'bazi_monthly' ? 'monthly' : 'daily';
                const bazi = getDayBaZiDetail(date, timeframe, baziMode);

                const parent = el.parentElement;
                const rect = parent?.getBoundingClientRect();
                const isNative = param.sourceEvent && 'clientX' in param.sourceEvent;
                const cursorX = isNative && rect ? (param.sourceEvent as MouseEvent).clientX - rect.left : (param.point?.x as number);
                const cursorY = isNative && rect ? (param.sourceEvent as MouseEvent).clientY - rect.top : (param.point?.y as number);
                const isUpper = priceAtY >= 0.5;

                // Hide immediately on move and only show if hovered in place for > 1 second (1000ms)
                el.style.display = 'none';
                baziHoverTimer = setTimeout(() => {
                  if (!el) return;
                  el.style.cssText = `
                    position: absolute !important;
                    display: block !important;
                    pointer-events: none !important;
                    z-index: 9999 !important;
                    left: 0 !important;
                    top: 0 !important;
                    transform: translate(${cursorX + 10}px, ${cursorY - 24}px) !important;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.4);
                    border-radius: 4px;
                    border: 1px solid rgba(255, 255, 255, 0.15);
                    background-color: rgba(15, 23, 42, 0.95);
                    padding: 4px 8px;
                    color: #f8fafc;
                    font-family: Inter, sans-serif;
                    font-size: 11px;
                    box-sizing: border-box !important;
                    white-space: nowrap !important;
                  `;

                  if (isUpper) {
                    // Hovering upper histogram: Element Utama (e.g. Air, Api, Kayu, Tanah, Logam)
                    el.innerHTML = `<span>${bazi.mainElementSimple}</span>`;
                  } else {
                    // Hovering lower histogram: Element Shio (e.g. Anjing (tanah), Monyet (logam))
                    el.innerHTML = `<span>${bazi.shioCombinedText}</span>`;
                  }
                }, 1000);
              };
              chart.subscribeCrosshairMove(handleBaziCrosshairMove);
              baziCrosshairUnsubRef.current[indicator.id] = () => {
                if (baziHoverTimer) clearTimeout(baziHoverTimer);
                try { chart.unsubscribeCrosshairMove(handleBaziCrosshairMove); } catch { }
              };

              setIndicatorSeriesReady(prev => ({ ...prev, [indicator.id]: true }));
            } else if (indicator.type === 'volume_histogram') {
              indicatorSeries = chart.addSeries(HistogramSeries, {
                priceFormat: { type: 'volume' },
                priceScaleId: 'right',
                color: indicator.color,
              }, targetPaneIndex);

              // Set data with color based on price movement, converting to Lot (/100) like in BrokerInventoryPage
              indicatorSeries.setData(filteredRows.map(d => ({
                time: d.time as any,
                value: (d.volume ?? 0) / 100,
                color: d.close >= d.open ? volumeHistogramSettings.upColor : volumeHistogramSettings.downColor,
              })));

              indicatorSeriesRefs.current[indicator.id] = indicatorSeries;
              setIndicatorSeriesReady(prev => ({ ...prev, [indicator.id]: true }));
            } else if (indicator.type === 'rsi') {
              indicatorSeries = chart.addSeries(LineSeries, {
                color: indicator.color || '#6c5ce7',
                lineWidth: 2,
                title: `RSI (${indicator.period || 14})`,
              }, targetPaneIndex);
              indicatorSeries.setData(indicatorData.map(d => ({ time: d.time as any, value: d.value })));

              if (rsiSettings.showOverbought) {
                indicatorSeries.createPriceLine({
                  price: rsiSettings.overbought || 70,
                  color: 'rgba(239, 68, 68, 0.6)',
                  lineWidth: 1,
                  lineStyle: 2,
                  title: 'OB',
                });
              }
              if (rsiSettings.showOversold) {
                indicatorSeries.createPriceLine({
                  price: rsiSettings.oversold || 30,
                  color: 'rgba(34, 197, 94, 0.6)',
                  lineWidth: 1,
                  lineStyle: 2,
                  title: 'OS',
                });
              }
              indicatorSeriesRefs.current[indicator.id] = indicatorSeries;
              setIndicatorSeriesReady(prev => ({ ...prev, [indicator.id]: true }));
            } else if (indicator.type === 'macd') {
              const macdCalc = calculateMACD(filteredRows, 12, 26, 9);
              indicatorSeries = chart.addSeries(LineSeries, {
                color: '#2962ff',
                lineWidth: 2,
                title: 'MACD',
              }, targetPaneIndex);
              indicatorSeries.setData(macdCalc.macd.map(d => ({ time: d.time as any, value: d.value })));

              const signalSeries = chart.addSeries(LineSeries, {
                color: '#ff6d00',
                lineWidth: 1,
                title: 'Signal',
              }, targetPaneIndex);
              signalSeries.setData(macdCalc.signal.map(d => ({ time: d.time as any, value: d.value })));

              const histSeries = chart.addSeries(HistogramSeries, {
                color: '#26a69a',
                priceFormat: { type: 'volume' },
              }, targetPaneIndex);
              histSeries.setData(macdCalc.histogram.map(d => ({ time: d.time as any, value: d.value })));

              if (!indicatorAuxRefs.current[indicator.id]) indicatorAuxRefs.current[indicator.id] = [];
              indicatorAuxRefs.current[indicator.id]?.push(signalSeries, histSeries);
              indicatorSeriesRefs.current[indicator.id] = indicatorSeries;
              setIndicatorSeriesReady(prev => ({ ...prev, [indicator.id]: true }));
            } else if (indicator.type === 'stochastic') {
              const stochCalc = calculateStochastic(filteredRows, indicator.period || 14, 3);
              indicatorSeries = chart.addSeries(LineSeries, {
                color: stochasticSettings.kColor || '#9b59b6',
                lineWidth: 2,
                title: '%K',
              }, targetPaneIndex);
              indicatorSeries.setData(stochCalc.k.map(d => ({ time: d.time as any, value: d.value })));

              const dSeries = chart.addSeries(LineSeries, {
                color: stochasticSettings.dColor || '#ffa726',
                lineWidth: 1,
                title: '%D',
              }, targetPaneIndex);
              dSeries.setData(stochCalc.d.map(d => ({ time: d.time as any, value: d.value })));

              if (!indicatorAuxRefs.current[indicator.id]) indicatorAuxRefs.current[indicator.id] = [];
              indicatorAuxRefs.current[indicator.id]?.push(dSeries);
              indicatorSeriesRefs.current[indicator.id] = indicatorSeries;
              setIndicatorSeriesReady(prev => ({ ...prev, [indicator.id]: true }));
            } else if (indicator.type === 'buy_sell_frequency') {
              const buySellData = calculateBuySellFrequency(filteredRows, bidAskData, indicator.period || 14);
              indicatorSeries = chart.addSeries(LineSeries, {
                color: '#16a34a',
                lineWidth: 2,
                title: 'Bid Freq',
              }, targetPaneIndex);
              indicatorSeries.setData(buySellData.buyFreq.map(d => ({ time: d.time as any, value: d.value })));

              const sellSeries = chart.addSeries(LineSeries, {
                color: '#dc2626',
                lineWidth: 2,
                title: 'Ask Freq',
              }, targetPaneIndex);
              sellSeries.setData(buySellData.sellFreq.map(d => ({ time: d.time as any, value: d.value })));

              if (!indicatorAuxRefs.current[indicator.id]) indicatorAuxRefs.current[indicator.id] = [];
              indicatorAuxRefs.current[indicator.id]?.push(sellSeries);
              indicatorSeriesRefs.current[indicator.id] = indicatorSeries;
              setIndicatorSeriesReady(prev => ({ ...prev, [indicator.id]: true }));
            }

            // Vertical line primitives are centrally managed by line 3180 useEffect
          } else if (indicator.type === 'daily_shio' || indicator.type === 'daily_element') {
            // For Daily Shio and Daily Element, collect markers to apply later
            if (!indicator.enabled) {
              return;
            }

            // First, get first row for each unique day to ensure precise timing
            const dayToFirstRow = new Map<string, { row: any; time: number }>();

            filteredRows.forEach(row => {
              const date = new Date(row.time * 1000);
              const dayKey = date.toDateString();

              // Store first occurrence of each day
              if (!dayToFirstRow.has(dayKey)) {
                dayToFirstRow.set(dayKey, { row, time: row.time });
              }
            });

            // Now create markers only once per unique day using the first row's timestamp
            // Convert to array and sort by time to get the most recent days first
            const sortedDays = Array.from(dayToFirstRow.entries())
              .sort((a, b) => b[1].time - a[1].time); // Sort descending (newest first)

            // Limit to dayLimit number of markers (most recent)
            const dayLimit = indicator.dayLimit || 30;
            const limitedDays = sortedDays.slice(0, dayLimit);

            let collectedCount = 0;

            for (const [dayKey, { time }] of limitedDays) {
              const date = new Date(time * 1000);

              // Calculate for this day using BaZi method from BaZiCycleAnalysis
              if (indicator.type === 'daily_shio') {
                const info = getDailyShioInfo(date, timeframe);
                const displayMode = indicator.displayMode || 'emoji';
                if (displayMode === 'shape') {
                  const itemName = info.shio;
                  const markerColor = indicator.shapeColors?.[itemName] || info.color;
                  const markerShape = indicator.shape || 'circle';
                  let tvShape: 'circle' | 'square' | 'arrowUp' | 'arrowDown' = 'circle';
                  if (markerShape === 'square') tvShape = 'square';
                  else if (markerShape === 'arrowUp') tvShape = 'arrowUp';
                  else if (markerShape === 'arrowDown') tvShape = 'arrowDown';

                  allMarkers.push({
                    time: time,
                    position: 'belowBar',
                    color: markerColor,
                    shape: tvShape,
                    size: 2,
                    id: `${indicator.type}_${dayKey}`,
                  } as any as SeriesMarker<any>);
                } else {
                  allMarkers.push({
                    time: time,
                    position: 'belowBar',
                    color: info.color,
                    shape: undefined,
                    text: info.emoji,
                    size: 2,
                    id: `${indicator.type}_${dayKey}`,
                  } as any as SeriesMarker<any>);
                }
              } else {
                const info = getDailyElementInfo(date, timeframe);
                const displayMode = indicator.displayMode || 'emoji';
                if (displayMode === 'shape') {
                  const itemName = info.element;
                  const markerColor = indicator.shapeColors?.[itemName] || info.color;
                  const markerShape = indicator.shape || 'circle';
                  let tvShape: 'circle' | 'square' | 'arrowUp' | 'arrowDown' = 'circle';
                  if (markerShape === 'square') tvShape = 'square';
                  else if (markerShape === 'arrowUp') tvShape = 'arrowUp';
                  else if (markerShape === 'arrowDown') tvShape = 'arrowDown';

                  allMarkers.push({
                    time: time,
                    position: 'aboveBar',
                    color: markerColor,
                    shape: tvShape,
                    size: 2,
                    id: `${indicator.type}_${dayKey}`,
                  } as any as SeriesMarker<any>);
                } else {
                  allMarkers.push({
                    time: time,
                    position: 'aboveBar',
                    color: info.color,
                    shape: undefined,
                    text: info.emoji,
                    size: 2,
                    id: `${indicator.type}_${dayKey}`,
                  } as any as SeriesMarker<any>);
                }
              }
              collectedCount++;
            }

            // Log only total, not per-indicator to avoid spam
            console.log(`✅ Collected ${collectedCount} markers for ${indicator.type}`);

            // Skip adding series for daily shio/element - we use markers instead
            indicatorRefs.current[indicator.id] = null;
            setIndicatorSeriesReady(prev => ({ ...prev, [indicator.id]: true }));
          } else if (indicatorData.length > 0) {
            const isForeignDomestic = indicator.type === 'foreign_domestic';

            if (isForeignDomestic) {
              // BaselineSeries colors the line/fill blue above zero (foreign net-buying) and
              // amber below zero (foreign net-selling / domestic net-buying) automatically.
              // Foreign/Domestic values (net buy/sell volume) are on a far larger scale than
              // candle prices, so it gets its own left-side price scale to avoid squashing the candles.
              indicatorSeries = chart.addSeries(BaselineSeries, {
                baseValue: { type: 'price', price: 0 },
                topLineColor: '#3b82f6',
                topFillColor1: 'transparent',
                topFillColor2: 'transparent',
                bottomLineColor: '#f59e0b',
                bottomFillColor1: 'transparent',
                bottomFillColor2: 'transparent',
                lineWidth: 2,
                lastValueVisible: true,
                priceLineVisible: false,
                priceScaleId: 'left',
                priceFormat: {
                  type: 'custom',
                  formatter: (price: number) => formatLotNumber(price),
                },
              });
              indicatorSeries.priceScale().applyOptions({ scaleMargins: { top: 0.1, bottom: 0.1 } });
              // Zero line — values above it mean foreign accumulation, below mean distribution
              indicatorSeries.createPriceLine({
                price: 0,
                color: colors.borderColor,
                lineWidth: 1,
                lineStyle: 0, // solid
                axisLabelVisible: false,
                title: '',
              });

              // Cursor-following legend — positioned & filled via direct DOM manipulation
              // (mirrors BrokerInventoryPage's hover tooltip), shown only while hovering.
              try { foreignDomesticCrosshairUnsubRef.current?.(); } catch { }
              const fdSeries = indicatorSeries;
              const handleFdCrosshairMove = (param: any) => {
                const el = foreignDomesticLegendRef.current;
                if (!el) return;

                if (placingIndicatorIdRef.current || !param || param.point === undefined || !param.time) {
                  el.style.display = 'none';
                  return;
                }
                const point = param?.seriesData?.get(fdSeries) as { value?: number; close?: number } | undefined;
                const trackedValue = point?.value ?? point?.close;
                if (trackedValue === undefined) {
                  el.style.display = 'none';
                  return;
                }

                // Helper to normalize dates to YYYY-MM-DD
                const getNormalizedDateStr = (timeVal: any): string => {
                  if (typeof timeVal === 'string') {
                    return timeVal.slice(0, 10);
                  } else if (typeof timeVal === 'number') {
                    const date = new Date(timeVal * 1000);
                    return date.toISOString().slice(0, 10);
                  } else if (timeVal && typeof timeVal === 'object') {
                    const t = timeVal as any;
                    const monthStr = String(t.month).padStart(2, '0');
                    const dayStr = String(t.day).padStart(2, '0');
                    return `${t.year}-${monthStr}-${dayStr}`;
                  }
                  return '';
                };

                const targetDateStr = getNormalizedDateStr(param.time);
                
                // Find corresponding index in indicatorData
                const currentIndex = indicatorData.findIndex(item => getNormalizedDateStr(item.time) === targetDateStr);
                
                 const cumVol = trackedValue;
                 const prevItem = currentIndex > 0 ? indicatorData[currentIndex - 1] : null;
                 const prevValue = prevItem ? (prevItem.value ?? 0) : 0;
                 
                 const perDateVol = cumVol - prevValue;
                 
                 const percentChange = prevValue !== 0 ? (perDateVol / Math.abs(prevValue)) * 100 : 0;

                 const formatDisplayDate = (dateStr: string): string => {
                   if (!dateStr) return '';
                   try {
                     const date = new Date(dateStr);
                     return date.toLocaleDateString('id-ID', {
                       day: '2-digit',
                       month: 'short',
                       year: 'numeric'
                     });
                   } catch {
                     return dateStr;
                   }
                 };
                 const displayDate = formatDisplayDate(targetDateStr);

                 const perDateVolText = (perDateVol >= 0 ? '+' : '') + formatLotNumber(perDateVol);
                 const perDateVolColor = perDateVol > 0 ? '#10b981' : (perDateVol < 0 ? '#ef4444' : '#9ca3af');
                 
                 const percentChangeText = (perDateVol >= 0 ? '+' : '') + percentChange.toFixed(2) + '%';
                 const percentChangeColor = perDateVol > 0 ? '#10b981' : (perDateVol < 0 ? '#ef4444' : '#9ca3af');

                 const parent = el.parentElement;
                 const rect = parent?.getBoundingClientRect();
                 const isNative = param.sourceEvent && 'clientX' in param.sourceEvent;
                 const cursorX = isNative && rect ? (param.sourceEvent as MouseEvent).clientX - rect.left : (param.point?.x as number);
                 const cursorY = isNative && rect ? (param.sourceEvent as MouseEvent).clientY - rect.top : (param.point?.y as number);

                 // Position offset
                 el.style.cssText = `
                   position: absolute !important;
                   display: block !important;
                   pointer-events: none !important;
                   z-index: 9999 !important;
                   left: 0 !important;
                   top: 0 !important;
                   transform: translate(${cursorX + 15}px, ${cursorY + 15}px) !important;
                   box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5);
                  border-radius: 6px;
                  border: 1px solid #1e293b;
                  background-color: rgba(15, 23, 42, 0.95);
                  padding: 10px;
                  color: #cbd5e1;
                  font-family: Inter, monospace;
                  font-size: 11px;
                  box-sizing: border-box !important;
                  width: 185px !important;
                `;

                const headerText = 'Foreign Flow';
                const headerColor = cumVol >= 0 ? '#3b82f6' : '#f59e0b';

                el.innerHTML = `
                  <div style="margin-bottom: 8px; font-weight: bold; color: #f8fafc; font-size: 13px; border-bottom: 1px solid #334155; padding-bottom: 6px;">
                    ${symbol ? `${symbol.toUpperCase()} - ` : ''}${displayDate}
                  </div>
                  
                  <div style="margin-bottom: 6px; font-weight: 600; color: ${headerColor}; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em;">
                    ${headerText}
                  </div>
                  
                  <div style="display: grid; grid-template-columns: auto 16px auto; row-gap: 4px; align-items: center;">
                    <span style="color: #94a3b8;">Cum Vol:</span>
                    <span></span>
                    <span style="font-weight: 600; color: #f3f4f6; text-align: right;">${formatLotNumber(cumVol)} Lot</span>
                    
                    <span style="color: #94a3b8;">Change:</span>
                    <span></span>
                    <span style="font-weight: 600; color: ${perDateVolColor}; text-align: right;">${perDateVolText} Lot</span>
                    
                    <span style="color: #94a3b8;">% Change:</span>
                    <span></span>
                    <span style="font-weight: 600; color: ${percentChangeColor}; text-align: right;">${percentChangeText}</span>
                  </div>
                `;
              };
              chart.subscribeCrosshairMove(handleFdCrosshairMove);
              foreignDomesticCrosshairUnsubRef.current = () => { try { chart.unsubscribeCrosshairMove(handleFdCrosshairMove); } catch { } };
            } else {
              indicatorSeries = chart.addSeries(LineSeries, {
                color: indicator.type === 'buy_sell_frequency' ? '#10b981' : indicator.color,
                lineWidth: 2,
                title: indicator.type === 'buy_sell_frequency' ? 'Bid Frequency' : indicator.name,
              });
            }

            indicatorSeries.setData(indicatorData.map(d => ({
              time: d.time as any,
              value: d.value
            })));

            console.log(`📊 Main chart ${indicator.type} series added successfully:`, {
              indicatorId: indicator.id,
              seriesType: 'LineSeries',
              dataPoints: indicatorData.length
            });

            // Mark this indicator series as ready
            setIndicatorSeriesReady(prev => ({ ...prev, [indicator.id]: true }));
          }

          indicatorRefs.current[indicator.id] = indicatorSeries;
        }
      });

      // Adjust pane stretch factors cleanly across all panes after all indicator series are attached
      try {
        const allPanes = chart.panes();
        if (allPanes && allPanes.length > 1) {
          const paneIndicators = indicators.filter(ind => ind.enabled && !ind.hidden && isPaneIndicator(ind));
          
          // Pane 0 is always Price
          if (allPanes[0]?.setStretchFactor) {
            allPanes[0].setStretchFactor(1000);
          }

          // Sub-panes 1..n dynamically match their respective indicator types
          paneIndicators.forEach((ind, idx) => {
            const pane = allPanes[1 + idx];
            if (pane && pane.setStretchFactor) {
              if (isBaZiType(ind.type)) {
                pane.setStretchFactor(135);
              } else {
                pane.setStretchFactor(300);
              }
            }
          });
        }
      } catch (e) {
        console.warn('Error adjusting pane stretch factors:', e);
      }

      // Apply all collected markers to the price series after all indicators are processed
      if (priceRef.current && allMarkers.length > 0) {
        const shioCount = allMarkers.filter(m => m.position === 'belowBar').length;
        const elementCount = allMarkers.filter(m => m.position === 'aboveBar').length;

        console.log('📊 MARKERS DEBUG:', {
          total: allMarkers.length,
          shio: shioCount,
          element: elementCount,
          enabledIndicators: indicators
            .filter(ind => ind.enabled && (ind.type === 'daily_shio' || ind.type === 'daily_element'))
            .map(ind => ind.type)
        });

        try {
          // Separate markers by position to avoid conflicts
          const belowBarMarkers = allMarkers.filter(m => m.position === 'belowBar');
          const aboveBarMarkers = allMarkers.filter(m => m.position === 'aboveBar');

          console.log('🔍 Markers breakdown:', {
            belowBar: belowBarMarkers.length,
            aboveBar: aboveBarMarkers.length,
            sampleBelow: belowBarMarkers[0],
            sampleAbove: aboveBarMarkers[0]
          });

          // Sort markers by time to ensure proper rendering
          const sortedMarkers = [...allMarkers].sort((a, b) => (a.time as number) - (b.time as number));

          // Apply all markers at once (TradingView should handle mixed positions)
          createSeriesMarkers(priceRef.current as any, sortedMarkers);
          console.log('✅ Markers applied successfully', {
            totalMarkers: sortedMarkers.length,
            firstMarker: sortedMarkers[0],
            lastMarker: sortedMarkers[sortedMarkers.length - 1]
          });
        } catch (error) {
          console.error('❌ Error applying markers:', error, error);
        }
      } else {
        console.log('📊 No markers to apply', {
          priceRef: !!priceRef.current,
          markersCount: allMarkers.length,
          enabledDailyIndicators: indicators
            .filter(ind => ind.enabled && (ind.type === 'daily_shio' || ind.type === 'daily_element'))
            .map(ind => ind.type)
        });
      }

      const isSameSymbolAndTimeframe = lastSymbolRef.current === symbol && lastTimeframeRef.current === timeframe;
      lastSymbolRef.current = symbol;
      lastTimeframeRef.current = timeframe;

      const rangeToApply = activeLogicalRange || savedLogicalRangeRef.current;

      if (isSameSymbolAndTimeframe && rangeToApply) {
        try {
          chart.timeScale().setVisibleLogicalRange(rangeToApply);
          console.log('📊 Preserved logical range applied:', rangeToApply);
        } catch (err) {
          console.warn('Failed to restore logical range:', err);
        }
      } else {
        savedLogicalRangeRef.current = null;
        chart.timeScale().fitContent();
        
        // Default visible range to the last 180 days from the latest data point
        if (filteredRows.length > 0) {
          const latestRow = filteredRows[filteredRows.length - 1];
          if (latestRow) {
            const latestTime = latestRow.time as number;
            const start180DaysAgo = latestTime - (180 * 24 * 60 * 60);
            chart.timeScale().setVisibleRange({
              from: start180DaysAgo as any,
              to: latestTime as any
            });
          }
        }
      }
      console.log(`📊 Chart fitContent called, plotted ${filteredRows.length} rows`);
      setPlotted(filteredRows.length);
      setErr(null);

      // Sync time scale with indicator charts
      syncTimeScaleWithIndicators();
    } catch (e: any) {
      setErr(e?.message ?? 'render error');
      setPlotted(0);
    }
  }, [filteredRows, chartColors, volumeHistogramSettings, rsiSettings, stochasticSettings, style, indicators, isLoadingData, bidAskData, foreignFlowData, isActive, computedWorkerResults, timeframe]);

  // Add chart listeners only once
  useEffect(() => {
    if (isActive === false) return;
    if (!chartRef.current || style === 'footprint' as ChartStyle) return;

    const chart = chartRef.current;

    // Add time scale change listener for synchronization (only once)
    if (!(chart as any)._timeScaleListenerAdded) {
      (chart as any)._timeScaleListenerAdded = true;
      const timeScale = chart.timeScale();
      const unsubscribeTimeScale = timeScale.subscribeVisibleLogicalRangeChange((logicalRange) => {
        if (logicalRange && !isSyncingTimeScaleRef.current) {
          isSyncingTimeScaleRef.current = true;
          try {
            Object.values(indicatorChartRefs.current).forEach(indicatorChart => {
              if (indicatorChart && (indicatorChart as any).timeScale) {
                (indicatorChart as any).timeScale().setVisibleLogicalRange(logicalRange);
              }
            });
          } finally {
            isSyncingTimeScaleRef.current = false;
          }
        }
      });

      // Sync crosshair from main chart to indicator charts (two-way sync)
      // Helper function to get crosshair data point (based on TradingView example)
      const getCrosshairDataPoint = (series: any, param: any) => {
        if (!param.time || !param.seriesData) {
          return null;
        }
        const dataPoint = param.seriesData.get(series);
        return dataPoint || null;
      };

      const unsubscribeCrosshair = chart.subscribeCrosshairMove((param) => {
        if (param) {
          let tSec: number | null = null;
          if (param.time) {
            if (typeof param.time === 'number') tSec = param.time;
            else if (typeof param.time === 'string') tSec = Math.floor(new Date(param.time).getTime() / 1000);
            else if (typeof param.time === 'object' && 'year' in param.time) {
              const t = param.time as any;
              tSec = Math.floor(Date.UTC(t.year, t.month - 1, t.day) / 1000);
            }
          } else if (param.point && chartRef.current) {
            try {
              const time = chartRef.current.timeScale().coordinateToTime(param.point.x as any);
              if (time) {
                if (typeof time === 'number') tSec = time;
                else if (typeof time === 'string') tSec = Math.floor(new Date(time).getTime() / 1000);
                else if (typeof time === 'object' && 'year' in time) {
                  const t = time as any;
                  tSec = Math.floor(Date.UTC(t.year, t.month - 1, t.day) / 1000);
                }
              }
            } catch {}
          }

          if (tSec) hoverTimeRef.current = tSec;

          if (placingIndicatorIdRef.current) {
            if (tSec && tSec !== lastPreviewTimeRef.current) {
              lastPreviewTimeRef.current = tSec;
              const currentPlacingId = placingIndicatorIdRef.current;
              const targetInd = indicatorsRef.current.find(i => i.id === currentPlacingId);
              if (targetInd) {
                const lineDates = calculateVerticalLineDates(
                  tSec,
                  targetInd.intervalDays || 15,
                  targetInd.lineCount || 12,
                  filteredRows,
                  targetInd.direction || 'forward'
                );
                const previewItems: VerticalLineItem[] = lineDates.map(item => ({
                  time: item.timestampSec,
                  label: item.label,
                  isT0: item.index === 0,
                  color: targetInd.color || '#8b5cf6',
                  opacity: (targetInd.opacity ?? 0.85) * 0.75,
                  isDashed: true,
                  index: item.index,
                }));
                setPreviewItems(previewItems);
              }
            } else if (!tSec && param.point === undefined) {
              // Cursor left canvas bounds - hide preview lines cleanly
              lastPreviewTimeRef.current = null;
              setPreviewItems([]);
            }
          }
        } else if (placingIndicatorIdRef.current) {
          lastPreviewTimeRef.current = null;
          setPreviewItems([]);
        }

        if (param && param.time && priceRef.current) {
          // Get data point from main chart price series
          const dataPoint = getCrosshairDataPoint(priceRef.current, param);

          // Sync with all indicator charts
          Object.entries(indicatorChartRefs.current).forEach(([_id, indicatorChart]) => {
            if (indicatorChart) {
              // Get all series from indicator chart and sync with the first one
              const series = (indicatorChart as any).series();
              if (series && series.length > 0) {
                const indicatorSeries = series[0];
                // Use the same data point for all indicator charts
                if (dataPoint) {
                  try {
                    indicatorChart.setCrosshairPosition(dataPoint.value, dataPoint.time, indicatorSeries);
                  } catch (e) {
                    // Silent fail
                  }
                } else {
                  try {
                    indicatorChart.clearCrosshairPosition();
                  } catch (e) {
                    // Silent fail
                  }
                }
              }
            }
          });
        }
      });

      // Store unsubscribe functions
      (chart as any)._timeScaleUnsubscribe = unsubscribeTimeScale;
      (chart as any)._crosshairUnsubscribe = unsubscribeCrosshair;
    }
  }, [filteredRows, indicators, volumeHistogramSettings, rsiSettings, stochasticSettings, chartColors, style, isActive, timeframe]);

  // Dedicated useEffect for native chart click subscription (T0 selection)
  useEffect(() => {
    if (!chartRef.current || style === ('footprint' as ChartStyle)) return;

    const mainChart = chartRef.current;
    const clickSubscribedCharts: any[] = [mainChart];

    const handleNativeClick = (param: any) => {
      const currentPlacingId = placingIndicatorIdRef.current;
      if (!currentPlacingId) return;

      console.log('📍 Native chart click triggered during placement:', param);

      let clickTimeSec: number | null = null;
      if (param && param.time) {
        if (typeof param.time === 'number') clickTimeSec = param.time;
        else if (typeof param.time === 'string') clickTimeSec = Math.floor(new Date(param.time).getTime() / 1000);
        else if (typeof param.time === 'object' && 'year' in param.time) {
          const t = param.time as any;
          clickTimeSec = Math.floor(Date.UTC(t.year, t.month - 1, t.day) / 1000);
        }
      }
      if (!clickTimeSec && param && param.point && mainChart) {
        try {
          const time = mainChart.timeScale().coordinateToTime(param.point.x as any);
          if (time) {
            if (typeof time === 'number') clickTimeSec = time;
            else if (typeof time === 'string') clickTimeSec = Math.floor(new Date(time).getTime() / 1000);
            else if (typeof time === 'object' && 'year' in time) {
              const t = time as any;
              clickTimeSec = Math.floor(Date.UTC(t.year, t.month - 1, t.day) / 1000);
            }
          }
        } catch {}
      }
      if (!clickTimeSec && lastPreviewTimeRef.current) {
        clickTimeSec = lastPreviewTimeRef.current;
      }
      if (!clickTimeSec && hoverTimeRef.current) {
        clickTimeSec = hoverTimeRef.current;
      }

      if (clickTimeSec) {
        const finalTargetTime = clickTimeSec;
        console.log(`📍 Locking T0 date: ${new Date(finalTargetTime * 1000).toISOString()} for indicator ${currentPlacingId}`);
        setIndicators(prev => prev.map(ind =>
          ind.id === currentPlacingId
            ? { ...ind, baseTime: finalTargetTime }
            : ind
        ));
        setPlacingIndicatorId(null);
      }
    };

    try {
      mainChart.subscribeClick(handleNativeClick);
    } catch (e) {
      console.warn('Failed to subscribeClick on main chart:', e);
    }

    // Also attach to sub-pane charts
    Object.values(indicatorChartRefs.current).forEach(subChart => {
      if (subChart && typeof subChart.subscribeClick === 'function') {
        try {
          subChart.subscribeClick(handleNativeClick);
          clickSubscribedCharts.push(subChart);
        } catch {}
      }
    });

    return () => {
      clickSubscribedCharts.forEach(c => {
        try {
          c.unsubscribeClick(handleNativeClick);
        } catch {}
      });
    };
  }, [style, filteredRows.length, indicators.length]);

  // Dedicated useEffect for Escape key & Right-click (contextmenu) placement cancellation
  useEffect(() => {
    if (!placingIndicatorId) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        console.log('🚫 Cancelled T0 placement via Escape key');
        setPlacingIndicatorId(null);
        lastPreviewTimeRef.current = null;
        setPreviewItems([]);
      }
    };

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      console.log('🚫 Cancelled T0 placement via right-click');
      setPlacingIndicatorId(null);
      lastPreviewTimeRef.current = null;
      setPreviewItems([]);
    };

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('contextmenu', handleContextMenu, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('contextmenu', handleContextMenu, true);
    };
  }, [placingIndicatorId, filteredRows]);

  // Synchronize all vertical line primitives (settled & preview) on main chart & all sub-panes
  useEffect(() => {
    if (style === ('footprint' as ChartStyle)) return;

    const allTargetSeries: Array<{ series: any; isMain: boolean }> = [];
    if (priceRef.current) {
      allTargetSeries.push({ series: priceRef.current, isMain: true });
    }
    Object.entries(indicatorSeriesRefs.current).forEach(([indId, subSeries]) => {
      if (subSeries && subSeries !== priceRef.current) {
        try {
          const ind = indicators.find(i => i.id === indId);
          if (!ind || (ind.enabled && !ind.hidden)) {
            allTargetSeries.push({ series: subSeries, isMain: false });
          }
        } catch {}
      }
    });

    // Detach all previous attached vertical line primitives
    attachedVertPrimitivesRef.current.forEach(({ series, primitive }) => {
      try { series.detachPrimitive(primitive); } catch {}
    });
    attachedVertPrimitivesRef.current = [];

    subPanePreviewPrimitivesRef.current = [];
    previewPrimitiveRef.current = null;
    vertLinePrimitivesRef.current = {};

    if (allTargetSeries.length === 0) return;

    const activeVertLineInds = indicators.filter(ind => ind.enabled && !ind.hidden && ind.type === 'vertical_line_15d' && ind.baseTime != null);

    activeVertLineInds.forEach(vertInd => {
      if (!vertLinePrimitivesRef.current[vertInd.id]) {
        vertLinePrimitivesRef.current[vertInd.id] = [];
      }

      const items = (placingIndicatorId != null && vertInd.id === placingIndicatorId)
        ? []
        : calculateVerticalLineDates(
            vertInd.baseTime!,
            vertInd.intervalDays || 15,
            vertInd.lineCount || 12,
            filteredRows,
            vertInd.direction || 'forward'
          ).map(item => ({
            time: item.timestampSec,
            label: item.label,
            isT0: item.index === 0,
            color: vertInd.color || '#8b5cf6',
            opacity: vertInd.opacity ?? 0.85,
            index: item.index,
          }));

      allTargetSeries.forEach(({ series, isMain }) => {
        const prim = new VerticalLinePrimitive(items, isMain, filteredRows);
        try {
          series.attachPrimitive(prim);
          attachedVertPrimitivesRef.current.push({ series, primitive: prim });
          vertLinePrimitivesRef.current[vertInd.id]?.push(prim);
        } catch {}
      });
    });

    if (placingIndicatorId != null) {
      allTargetSeries.forEach(({ series, isMain }) => {
        const previewPrim = new VerticalLinePrimitive([], isMain, filteredRows);
        try {
          series.attachPrimitive(previewPrim);
          attachedVertPrimitivesRef.current.push({ series, primitive: previewPrim });
          if (isMain) {
            previewPrimitiveRef.current = previewPrim;
          } else {
            subPanePreviewPrimitivesRef.current.push(previewPrim);
          }
        } catch {}
      });
    }

    if (chartRef.current) {
      try { (chartRef.current as any)._timeScale?.applyOptions({}); } catch {}
    }
    Object.values(indicatorChartRefs.current).forEach(subChart => {
      if (subChart) {
        try { (subChart as any)._timeScale?.applyOptions({}); } catch {}
      }
    });
  }, [placingIndicatorId, indicators, filteredRows, style, indicatorSeriesReady]);

  // Separate useEffect for footprint cleanup and chart recreation
  useEffect(() => {
    if (isActive === false) return;
    if (style === 'footprint' as ChartStyle && chartRef.current) {
      console.log('🧹 Cleaning up chart for footprint style');
      // Clean up series refs first
      priceRef.current = null;
      volRef.current = null;
      // Then remove chart
      chartRef.current.remove();
      chartRef.current = null;
    } else if (style !== 'footprint' as ChartStyle && !chartRef.current) {
      console.log('🔄 Force recreating chart after footprint switch');
      // Force recreation by resetting state
      setPlotted(0);
      setErr('');
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        // Trigger chart recreation by updating a dependency
        setPlotted(0);
      }, 50);
    }
  }, [style, isActive]);

  // Resize chart and separate indicator charts when container size or viewport height changes
  useEffect(() => {
    if (isActive === false) return;
    
    // Resize main chart
    if (chartRef.current && containerRef.current) {
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      if (width > 0 && height > 0) {
        chartRef.current.resize(width, height);
      }
    }

    // Resize separate indicator charts
    Object.entries(indicatorChartRefs.current).forEach(([indicatorId, indicatorChart]) => {
      if (indicatorChart) {
        const container = indicatorContainerRefs.current[indicatorId];
        if (container) {
          const width = container.clientWidth;
          const indObj = indicators.find(i => i.id === indicatorId);
          const height = isBaZiType(indObj?.type) ? Math.max(50, Math.round(indicatorChartHeight * 0.5)) : indicatorChartHeight;
          if (width > 0 && height > 0) {
            indicatorChart.resize(width, height);
          }
        }
      }
    });
  }, [chartViewportHeight, style, indicators, isActive]);

  // Reload indicator chart if series not ready after timeout
  useEffect(() => {
    const enabledBuySellIndicators = indicators.filter(ind =>
      ind.enabled && ind.type === 'buy_sell_frequency' && !indicatorSeriesReady[ind.id]
    );

    if (enabledBuySellIndicators.length === 0 || bidAskData.length === 0 || isLoadingData) {
      return;
    }

    // Set timeout to reload if series not ready after 5 seconds
    const timeoutId = setTimeout(() => {
      enabledBuySellIndicators.forEach(indicator => {
        if (!indicatorSeriesReady[indicator.id] && bidAskData.length > 0) {
          console.log(`⚠️ Indicator ${indicator.id} series not ready after timeout, triggering reload...`);

          // Remove existing chart
          if (indicatorChartRefs.current[indicator.id]) {
            const chart = indicatorChartRefs.current[indicator.id];
            if ((chart as any)._timeScaleUnsubscribe) {
              (chart as any)._timeScaleUnsubscribe();
            }
            chart?.remove();
            delete indicatorChartRefs.current[indicator.id];
          }

          // Trigger re-render by updating state
          setPlotted(prev => prev + 1);
        }
      });
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [indicators, indicatorSeriesReady, bidAskData, isLoadingData]);

  // Create separate charts for indicators
  useEffect(() => {
    // Skip indicator charts for footprint style
    if (style === 'footprint' as ChartStyle) {
      return;
    }

    const enabledSeparateIndicators = indicators.filter(ind => ind.enabled && isIndicatorSeparate(ind));

    console.log('📊 Processing separate indicators:', {
      totalIndicators: indicators.length,
      enabledSeparateIndicators: enabledSeparateIndicators.length,
      bidAskDataAvailable: bidAskData.length > 0
    });

    indicators.forEach((indicator) => {
      console.log(`📊 Processing separate indicator: ${indicator.type} (${indicator.id})`, {
        enabled: indicator.enabled,
        separateScale: isIndicatorSeparate(indicator),
        period: indicator.period
      });
      // Remove chart if indicator is disabled
      if (!indicator.enabled) {
        delete indicatorSeriesRefs.current[indicator.id];
        if (indicatorChartRefs.current[indicator.id]) {
          const chart = indicatorChartRefs.current[indicator.id];
          if ((chart as any)._timeScaleUnsubscribe) {
            (chart as any)._timeScaleUnsubscribe();
          }
          chart?.remove();
          delete indicatorChartRefs.current[indicator.id];
        }
        return;
      }

      if (!isIndicatorSeparate(indicator)) return;

      // Find index in enabled separate indicators
      const separateIndex = enabledSeparateIndicators.findIndex(ind => ind.id === indicator.id);

      const container = indicatorContainerRefs.current[indicator.id];
      if (!container) return;

      // Only remove existing chart if it doesn't exist or data has changed significantly
      if (indicatorChartRefs.current[indicator.id]) {
        const existingChart = indicatorChartRefs.current[indicator.id];
        // Check if we need to recreate the chart (only for indicators whose external data just arrived/changed)
        if (
          indicator.type === 'buy_sell_frequency' && bidAskData.length > 0
        ) {
          console.log(`📊 Recreating chart for ${indicator.type} due to data change`);
          existingChart?.remove();
          delete indicatorChartRefs.current[indicator.id];
        } else {
          // For other indicators, just update the data without recreating
          console.log(`📊 Updating existing chart for ${indicator.type}`);
          return;
        }
      }

      // Create new chart
      const colors = getThemeColors();
      const indicatorChart = createChart(container, {
        width: container.clientWidth || 400,
        height: isBaZiType(indicator.type) ? Math.max(50, Math.round(indicatorChartHeight * 0.5)) : indicatorChartHeight,
        layout: {
          background: { type: ColorType.Solid, color: 'transparent' },
          textColor: colors.axisTextColor
        },
        grid: {
          horzLines: { visible: false },
          vertLines: { visible: false }
        },
        rightPriceScale: {
          borderColor: colors.borderColor
        },
        timeScale: {
          borderColor: colors.borderColor,
          visible: separateIndex === enabledSeparateIndicators.length - 1
        },
        crosshair: { mode: style === 'footprint' as ChartStyle ? CrosshairMode.Hidden : CrosshairMode.Normal },
      });

      // Calculate indicator data
      console.log(`📊 Calculating separate indicator data for ${indicator.type}:`, {
        filteredRowsLength: filteredRows.length,
        bidAskDataLength: bidAskData.length,
        period: indicator.period
      });

      let indicatorData: IndicatorData[] = [];
      switch (indicator.type) {
        case 'rsi':
          indicatorData = calculateRSI(filteredRows, indicator.period);
          break;
        case 'macd':
          const macdData = calculateMACD(filteredRows, 12, 26, 9);
          indicatorData = macdData.macd;
          break;
        case 'stochastic':
          const stochasticData = calculateStochastic(filteredRows, indicator.period, 3);
          // For separate chart, we'll use %K line
          indicatorData = stochasticData.k;
          break;
        case 'volume_histogram':
          indicatorData = calculateVolumeHistogram(filteredRows);
          break;
        case 'bazi_indicator':
        case 'bazi_daily':
        case 'bazi_monthly': {
          const baziModeSep = indicator.type === 'bazi_monthly' ? 'monthly' : 'daily';
          indicatorData = calculateBaZiIndicator(filteredRows, timeframe, baziModeSep).data;
          break;
        }
        case 'buy_sell_frequency':
          console.log('📊 Separate Chart Buy/Sell Frequency - Input Data:', {
            filteredRowsLength: filteredRows.length,
            bidAskDataLength: bidAskData.length,
            period: indicator.period,
            sampleBidAskData: bidAskData.slice(0, 3)
          });
          const buySellDataSeparate = calculateBuySellFrequency(filteredRows, bidAskData, indicator.period);
          console.log('📊 Separate Chart Buy/Sell Frequency Data:', {
            buyFreqLength: buySellDataSeparate.buyFreq.length,
            sellFreqLength: buySellDataSeparate.sellFreq.length,
            sampleBuyFreq: buySellDataSeparate.buyFreq.slice(0, 3),
            sampleSellFreq: buySellDataSeparate.sellFreq.slice(0, 3)
          });
          // Use buy frequency as main indicator data
          indicatorData = buySellDataSeparate.buyFreq;
          break;
      }

      console.log(`📊 Separate Indicator ${indicator.type} data check:`, {
        indicatorId: indicator.id,
        dataLength: indicatorData.length,
        hasData: indicatorData.length > 0,
        sampleData: indicatorData.slice(0, 3)
      });

      if (indicatorData.length > 0) {
        console.log(`📊 Adding ${indicator.type} series to separate chart:`, {
          indicatorId: indicator.id,
          dataLength: indicatorData.length,
          sampleData: indicatorData.slice(0, 3),
          chartExists: !!indicatorChart
        });

        // Add indicator series
        let indicatorSeries: any;
        let shioSeries: any = null;
        if (isBaZiType(indicator.type)) {
          const baziModeSep = indicator.type === 'bazi_monthly' ? 'monthly' : 'daily';
          const { mainElementData, shioElementData, textItems } = calculateBaZiIndicator(filteredRows, timeframe, baziModeSep);
          // Layer 1 (Element Utama: 0 to 1.0)
          indicatorSeries = indicatorChart.addSeries(HistogramSeries, {
            priceFormat: { type: 'volume' },
            priceScaleId: 'right',
            color: indicator.color,
            visible: !indicator.hidden,
            lastValueVisible: false,
            priceLineVisible: false,
            autoscaleInfoProvider: () => ({
              priceRange: { minValue: 0, maxValue: 1.0 },
              margins: { above: 0, below: 0 },
            }),
          });
          indicatorSeries.priceScale().applyOptions({ scaleMargins: { top: 0, bottom: 0 } });
          indicatorSeries.setData(mainElementData as any);

          // Layer 2 (Element Shio: 0 to 0.5)
          shioSeries = indicatorChart.addSeries(HistogramSeries, {
            priceFormat: { type: 'volume' },
            priceScaleId: 'right',
            color: indicator.color,
            visible: !indicator.hidden,
            lastValueVisible: false,
            priceLineVisible: false,
            autoscaleInfoProvider: () => ({
              priceRange: { minValue: 0, maxValue: 1.0 },
              margins: { above: 0, below: 0 },
            }),
          });
          shioSeries.priceScale().applyOptions({ scaleMargins: { top: 0, bottom: 0 } });
          shioSeries.setData(shioElementData as any);

          // Attach BaZi text primitive
          const baziTextPrimitive = new BaZiTextPrimitive(textItems);
          shioSeries.attachPrimitive(baziTextPrimitive);
          indicatorSeriesRefs.current[indicator.id] = shioSeries;

          setIndicatorSeriesReady(prev => ({ ...prev, [indicator.id]: true }));
        } else if (indicator.type === 'volume_histogram') {
          // Use HistogramSeries for volume histogram
          indicatorSeries = indicatorChart.addSeries(HistogramSeries, {
            priceFormat: { type: 'volume' },
            priceScaleId: 'right',
            color: indicator.color,
            visible: !indicator.hidden,
          });
          indicatorSeries.priceScale().applyOptions({ scaleMargins: { top: 0.1, bottom: 0 } });

          // Set data with color based on price movement, converting to Lot (/100) like in BrokerInventoryPage
          indicatorSeries.setData(filteredRows.map(d => ({
            time: d.time as any,
            value: (d.volume ?? 0) / 100,
            color: d.close >= d.open ? volumeHistogramSettings.upColor : volumeHistogramSettings.downColor,
          })));
          indicatorSeriesRefs.current[indicator.id] = indicatorSeries;

          setIndicatorSeriesReady(prev => ({ ...prev, [indicator.id]: true }));
        } else {
          // Use LineSeries for other indicators
          indicatorSeries = indicatorChart.addSeries(LineSeries, {
            color: indicator.type === 'buy_sell_frequency' ? '#10b981' : indicator.color, // Green for bid frequency
            lineWidth: 2,
            title: indicator.type === 'buy_sell_frequency' ? 'Bid Frequency' : (indicator.name === 'VOLUME_HISTOGRAM' ? 'Volume Histogram' : (indicator.name === 'Foreign' ? 'Foreign Flow' : indicator.name)),
            visible: !indicator.hidden,
          });

          // Convert data format for lightweight-charts
          const chartData = indicatorData.map(d => ({
            time: d.time as any,
            value: d.value
          }));

          indicatorSeries.setData(chartData);
          indicatorSeriesRefs.current[indicator.id] = indicatorSeries;

          console.log(`📊 Separate chart ${indicator.type} series added successfully:`, {
            indicatorId: indicator.id,
            seriesType: 'LineSeries',
            dataPoints: indicatorData.length
          });

          // Mark this indicator series as ready
          setIndicatorSeriesReady(prev => ({ ...prev, [indicator.id]: true }));
        }

        // Add reference lines for RSI
        if (indicator.type === 'rsi') {
          // Overbought line
          if (rsiSettings.showOverbought) {
            indicatorSeries.createPriceLine({
              price: rsiSettings.overbought,
              color: '#ff6b6b',
              lineWidth: 1,
              lineStyle: 2, // dashed
              axisLabelVisible: true,
              title: 'Overbought'
            });
          }

          // Oversold line
          if (rsiSettings.showOversold) {
            indicatorSeries.createPriceLine({
              price: rsiSettings.oversold,
              color: '#4ecdc4',
              lineWidth: 1,
              lineStyle: 2, // dashed
              axisLabelVisible: true,
              title: 'Oversold'
            });
          }
        }

        // Add reference lines for Stochastic Oscillator
        if (indicator.type === 'stochastic') {
          // Overbought line (80)
          indicatorSeries.createPriceLine({
            price: 80,
            color: '#ff6b6b',
            lineWidth: 1,
            lineStyle: 2, // dashed
            axisLabelVisible: true,
            title: 'Overbought (80)'
          });

          // Oversold line (20)
          indicatorSeries.createPriceLine({
            price: 20,
            color: '#4ecdc4',
            lineWidth: 1,
            lineStyle: 2, // dashed
            axisLabelVisible: true,
            title: 'Oversold (20)'
          });

          // Add %D line (signal line)
          const stochasticData = calculateStochastic(filteredRows, indicator.period, 3);
          if (stochasticData.d.length > 0) {
            const dSeries = indicatorChart.addSeries(LineSeries, {
              color: stochasticSettings.dColor,
              lineWidth: 2,
              title: '%D (Signal)',
              visible: !indicator.hidden,
            });

            const dChartData = stochasticData.d.map(d => ({
              time: d.time as any,
              value: d.value
            }));

            dSeries.setData(dChartData);
          }
        }

        // Add zero line and signal line for MACD
        if (indicator.type === 'macd') {
          indicatorSeries.createPriceLine({
            price: 0,
            color: '#666',
            lineWidth: 1,
            lineStyle: 1,
            axisLabelVisible: !indicator.hidden,
            title: indicator.hidden ? '' : 'Zero'
          });

          // Add signal line
          const macdData = calculateMACD(filteredRows, 12, 26, 9);
          if (macdData.signal.length > 0) {
            const signalSeries = indicatorChart.addSeries(LineSeries, {
              color: '#ff6b6b',
              lineWidth: 2,
              title: 'MACD Signal',
              visible: !indicator.hidden,
            });

            const signalChartData = macdData.signal.map(s => ({
              time: s.time as any,
              value: s.value
            }));

            signalSeries.setData(signalChartData);
          }
        }

        // Add sell frequency line for Buy/Sell Frequency in separate chart
        if (indicator.type === 'buy_sell_frequency') {
          const buySellData = calculateBuySellFrequency(filteredRows, bidAskData, indicator.period);
          if (buySellData.sellFreq.length > 0) {
            const sellSeries = indicatorChart.addSeries(LineSeries, {
              color: '#ef4444', // Red color for ask/sell frequency
              lineWidth: 2,
              title: 'Ask Frequency',
              visible: !indicator.hidden,
            });

            const sellChartData = buySellData.sellFreq.map(s => ({
              time: s.time as any,
              value: s.value
            }));

            sellSeries.setData(sellChartData);
          }
        }

        indicatorChartRefs.current[indicator.id] = indicatorChart;

        // Synchronize time scale with main chart
        if (chartRef.current) {
          const mainTimeScale = chartRef.current.timeScale();
          const indicatorTimeScale = indicatorChart.timeScale();

          // Sync initial visible logical range
          const currentMainLogicalRange = mainTimeScale.getVisibleLogicalRange();
          if (currentMainLogicalRange) {
            isSyncingTimeScaleRef.current = true;
            try {
              indicatorTimeScale.setVisibleLogicalRange(currentMainLogicalRange);
            } finally {
              isSyncingTimeScaleRef.current = false;
            }
          }

          // Listen to indicator chart time scale changes (two-way sync)
          const unsubscribeSub = indicatorTimeScale.subscribeVisibleLogicalRangeChange((logicalRange) => {
            if (logicalRange && chartRef.current && !isSyncingTimeScaleRef.current) {
              isSyncingTimeScaleRef.current = true;
              try {
                chartRef.current.timeScale().setVisibleLogicalRange(logicalRange);
                Object.entries(indicatorChartRefs.current).forEach(([id, otherChart]) => {
                  if (id !== indicator.id && otherChart && (otherChart as any).timeScale) {
                    (otherChart as any).timeScale().setVisibleLogicalRange(logicalRange);
                  }
                });
              } finally {
                isSyncingTimeScaleRef.current = false;
              }
            }
          });
          (indicatorChart as any)._timeScaleUnsubscribe = unsubscribeSub;
        }

          // Sync crosshair/hover cursor (two-way sync)
          // Helper function to get crosshair data point (based on TradingView example)
          const getCrosshairDataPoint = (series: any, param: any) => {
            if (!param.time || !param.seriesData) {
              return null;
            }
            const dataPoint = param.seriesData.get(series);
            return dataPoint || null;
          };

          // Helper function to sync crosshair between charts
          const syncCrosshair = (targetChart: any, targetSeries: any, dataPoint: any) => {
            if (dataPoint && targetChart) {
              try {
                targetChart.setCrosshairPosition(dataPoint.value, dataPoint.time, targetSeries);
              } catch (e) {
                // Silent fail
              }
            } else {
              try {
                targetChart?.clearCrosshairPosition();
              } catch (e) {
                // Silent fail
              }
            }
          };

          const unsubscribeCrosshairIndicator = indicatorChart.subscribeCrosshairMove((param) => {
            if (param && param.time && chartRef.current && indicatorSeries) {
              // Get data point from indicator series
              const dataPoint = getCrosshairDataPoint(indicatorSeries, param);

              // Sync main chart crosshair
              if (priceRef.current) {
                syncCrosshair(chartRef.current, priceRef.current, dataPoint);
              }

              // Sync other indicator charts crosshair
              Object.entries(indicatorChartRefs.current).forEach(([id, otherChart]) => {
                if (id !== indicator.id && otherChart && otherChart !== indicatorChart) {
                  const otherIndicatorSeries = (otherChart as any).series()[0];
                  if (otherIndicatorSeries) {
                    syncCrosshair(otherChart, otherIndicatorSeries, dataPoint);
                  }
                }
              });
            }
          });

          // Store unsubscribe functions for cleanup
          (indicatorChart as any)._crosshairUnsubscribe = unsubscribeCrosshairIndicator;
      }
    });

    // Cleanup function to prevent memory leaks
    return () => {
      console.log('📊 Cleaning up indicator charts to prevent memory leaks');
      Object.entries(indicatorChartRefs.current).forEach(([id, chart]) => {
        if (chart) {
          try {
            // Unsubscribe from time scale changes
            if ((chart as any)._timeScaleUnsubscribe) {
              (chart as any)._timeScaleUnsubscribe();
            }
            // Unsubscribe from crosshair changes
            if ((chart as any)._crosshairUnsubscribe) {
              (chart as any)._crosshairUnsubscribe();
            }
            // Remove chart
            chart.remove();
          } catch (error) {
            console.warn(`Error cleaning up chart ${id}:`, error);
          }
        }
      });
      // Clear all chart references
      indicatorChartRefs.current = {};
    };
  }, [filteredRows, indicators, indicatorChartHeight, rsiSettings, volumeHistogramSettings, bidAskData]);

  // Stabilize indicator chart containers when data changes
  useEffect(() => {
    // Force re-layout of indicator containers to prevent positioning issues
    const enabledIndicators = indicators.filter(ind => ind.enabled && isIndicatorSeparate(ind));

    enabledIndicators.forEach(indicator => {
      const container = indicatorContainerRefs.current[indicator.id];
      if (container) {
        // Force reflow to stabilize layout
        container.style.transform = 'translateZ(0)';
        setTimeout(() => {
          container.style.transform = '';
        }, 0);
      }
    });
  }, [bidAskData, indicators]);

  // Check if indicator series are visible in viewport and auto-reload if needed
  useEffect(() => {
    const enabledIndicators = indicators.filter(ind => ind.enabled && isIndicatorSeparate(ind));

    enabledIndicators.forEach(indicator => {
      const chart = indicatorChartRefs.current[indicator.id];
      if (!chart || !indicatorSeriesReady[indicator.id] || indicatorReloaded[indicator.id]) {
        return; // Skip if chart not ready, series not ready, or already reloaded
      }

      // Check visibility after a delay to allow chart to render
      const timeoutId = setTimeout(() => {
        try {
          if (!chartRef.current) return;

          // Get visible range from main chart
          const mainTimeScale = chartRef.current.timeScale();
          const visibleRange = mainTimeScale.getVisibleRange();

          if (!visibleRange) return;

          // Get all series from indicator chart
          const series = (chart as any).series();
          if (!series || series.length === 0) return;

          // Check if any series has data in visible range
          let hasVisibleData = false;
          for (const s of series) {
            const data = s.data();
            if (!data || data.length === 0) continue;

            // Check if any data point is in visible range
            const hasDataInRange = data.some((point: any) => {
              const pointTime = point.time;
              return pointTime >= visibleRange.from && pointTime <= visibleRange.to;
            });

            if (hasDataInRange) {
              hasVisibleData = true;
              break;
            }
          }

          console.log(`📊 Indicator ${indicator.id} visibility check:`, {
            hasVisibleData,
            visibleRange: { from: visibleRange.from, to: visibleRange.to },
            seriesCount: series.length,
            alreadyReloaded: indicatorReloaded[indicator.id]
          });

          // If series is not visible and hasn't been reloaded yet, trigger invisible reload
          if (!hasVisibleData && !indicatorReloaded[indicator.id]) {
            console.log(`⚠️ Indicator ${indicator.id} series not visible in viewport, triggering invisible reload...`);

            // Mark as reloaded to prevent multiple reloads
            setIndicatorReloaded(prev => ({ ...prev, [indicator.id]: true }));

            // Mark series as not ready
            setIndicatorSeriesReady(prev => ({ ...prev, [indicator.id]: false }));

            // Unload chart invisibly
            if (chart) {
              try {
                if ((chart as any)._timeScaleUnsubscribe) {
                  (chart as any)._timeScaleUnsubscribe();
                }
                chart.remove();
              } catch (error) {
                console.warn(`Error removing chart ${indicator.id}:`, error);
              }
            }
            delete indicatorChartRefs.current[indicator.id];

            // Trigger re-render after a short delay (invisible)
            setTimeout(() => {
              console.log(`🔄 Invisible reloading indicator ${indicator.id}...`);
              setPlotted(prev => prev + 1);
            }, 100);
          }
        } catch (error) {
          console.warn(`Error checking visibility for indicator ${indicator.id}:`, error);
        }
      }, 3000); // Check after 3 seconds

      return () => clearTimeout(timeoutId);
    });
  }, [indicators, indicatorSeriesReady, indicatorReloaded]);

  // Resize responsif
  useEffect(() => {
    // Skip resize observer for footprint style
    if (style === 'footprint' as ChartStyle) {
      return;
    }

    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const updateChartSize = () => {
      if (!chartRef.current || !el) return;
      const cr = el.getBoundingClientRect();
      const newWidth = Math.max(1, Math.floor(cr.width));
      const newHeight = Math.max(1, Math.floor(cr.height));
      chartRef.current.applyOptions({
        width: newWidth,
        height: newHeight,
      });
    };

    // Force resize immediately when effect runs
    if (chartRef.current) {
      updateChartSize();
    }

    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (!cr || !chartRef.current || !el) return;

      const newWidth = Math.max(1, Math.floor(el.getBoundingClientRect().width));
      const newHeight = Math.max(1, Math.floor(el.getBoundingClientRect().height));

      chartRef.current.applyOptions({
        width: newWidth,
        height: newHeight,
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [style, filteredRows.length]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cleanup main chart subscriptions
      if (chartRef.current) {
        if ((chartRef.current as any)._timeScaleUnsubscribe) {
          (chartRef.current as any)._timeScaleUnsubscribe();
        }
        if ((chartRef.current as any)._crosshairUnsubscribe) {
          (chartRef.current as any)._crosshairUnsubscribe();
        }
        if ((chartRef.current as any)._clickUnsubscribe) {
          (chartRef.current as any)._clickUnsubscribe();
        }
      }

      // Cleanup indicator chart subscriptions
      Object.values(indicatorChartRefs.current).forEach(chart => {
        if (chart && (chart as any)._timeScaleUnsubscribe) {
          (chart as any)._timeScaleUnsubscribe();
        }
      });
    };
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!placingIndicatorId || !containerRef.current || !chartRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;

    try {
      const time = chartRef.current.timeScale().coordinateToTime(x as any);
      if (time) {
        let tSec: number | null = null;
        if (typeof time === 'number') tSec = time;
        else if (typeof time === 'string') tSec = Math.floor(new Date(time).getTime() / 1000);
        else if (typeof time === 'object' && 'year' in time) {
          const t = time as any;
          tSec = Math.floor(Date.UTC(t.year, t.month - 1, t.day) / 1000);
        }
        if (tSec) {
          hoverTimeRef.current = tSec;
          if (tSec !== lastPreviewTimeRef.current) {
            lastPreviewTimeRef.current = tSec;
            const currentPlacingId = placingIndicatorIdRef.current;
            const targetInd = indicatorsRef.current.find(i => i.id === currentPlacingId);
            if (targetInd) {
              const lineDates = calculateVerticalLineDates(
                tSec,
                targetInd.intervalDays || 15,
                targetInd.lineCount || 12,
                filteredRows,
                targetInd.direction || 'forward'
              );
              const previewItems: VerticalLineItem[] = lineDates.map(item => ({
                time: item.timestampSec,
                label: item.label,
                isT0: item.index === 0,
                color: targetInd.color || '#8b5cf6',
                opacity: (targetInd.opacity ?? 0.85) * 0.75,
                isDashed: true,
                index: item.index,
              }));
              if (previewPrimitiveRef.current) {
                previewPrimitiveRef.current.setData(previewItems, filteredRows);
              }
            }
          }
        }
      }
    } catch {}
  };

  const handleChartClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!placingIndicatorId || !chartRef.current || !containerRef.current) return;

    let targetTime: number | null = lastPreviewTimeRef.current || hoverTimeRef.current || hoverTime;

    if (!targetTime) {
      const rect = containerRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      try {
        const time = chartRef.current.timeScale().coordinateToTime(clickX as any);
        if (time) {
          if (typeof time === 'number') targetTime = time;
          else if (typeof time === 'string') targetTime = Math.floor(new Date(time).getTime() / 1000);
          else if (typeof time === 'object' && 'year' in time) {
            const t = time as any;
            targetTime = Math.floor(Date.UTC(t.year, t.month - 1, t.day) / 1000);
          }
        }
      } catch {}
    }

    if (!targetTime && filteredRows.length > 0) {
      targetTime = filteredRows[filteredRows.length - 1]?.time ?? null;
    }

    if (targetTime) {
      const finalTargetTime = targetTime;
      setIndicators(prev => prev.map(ind =>
        ind.id === placingIndicatorId
          ? { ...ind, baseTime: finalTargetTime }
          : ind
      ));
      setPlacingIndicatorId(null);
    }
  };

  const latest = filteredRows.at(-1);
  const prev = filteredRows.length > 1 ? filteredRows[filteredRows.length - 2] : undefined;
  const chg = latest && prev ? latest.close - prev.close : 0;
  const chgPct = latest && prev ? (chg / prev.close) * 100 : 0;

  // Sync style from parent when provided
  useEffect(() => {
    if (styleProp && styleProp !== style) {
      setStyle(styleProp);
    }
  }, [styleProp, style]);

  // Sync timeframe from parent when provided (but don't override user changes)
  const [isUserControlled, setIsUserControlled] = useState(false);

  useEffect(() => {
    if (!isUserControlled && timeframeProp && timeframeProp !== timeframe) {
      setTimeframe(timeframeProp as Timeframe);
    }
  }, [timeframeProp, timeframe, isUserControlled]);

  // Refresh chart on orientation change and window resize
  useEffect(() => {
    const handleResize = () => {
      if (!chartRef.current || !containerRef.current) return;

      const cr = containerRef.current.getBoundingClientRect();
      const newWidth = Math.max(1, Math.floor(cr.width));
      const newHeight = Math.max(1, Math.floor(cr.height));

      // Force resize
      chartRef.current.applyOptions({
        width: newWidth,
        height: newHeight,
      });

      // Force layout update
      setTimeout(() => {
        chartRef.current?.timeScale().fitContent();
      }, 150);
    };

    // Debounce resize to avoid performance issues
    let timeoutId: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 100);
    };

    window.addEventListener('orientationchange', handleResize);
    window.addEventListener('resize', debouncedResize);

    return () => {
      window.removeEventListener('orientationchange', handleResize);
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(timeoutId);
    };
  }, [style]);

  const hasLeftAxis = indicators.some(ind => ind.enabled && !isIndicatorSeparate(ind) && ind.type === 'foreign_domestic');

  return (
    <div className="flex flex-col h-full">
      <style>{`
        #tv-attr-logo {
          display: none !important;
        }
        
        /* Stabilize indicator chart containers to prevent positioning issues */
        .indicator-chart-container {
          position: relative !important;
          overflow: hidden !important;
          contain: layout style paint !important;
          transform: translateZ(0) !important; /* Force hardware acceleration */
        }
        
        .indicator-card {
          flex-shrink: 0 !important;
          position: relative !important;
          contain: layout style paint !important;
        }
        
        .indicator-header {
          flex-shrink: 0 !important;
          position: relative !important;
          contain: layout style paint !important;
        }
      `}</style>

      <Card className="flex-1 p-0 relative overflow-hidden rounded border border-border flex flex-col bg-card" style={{ padding: 0, margin: 0, height: '100%', minHeight: '400px' }}>
        {!hideControls && (
          <div className="bg-card border-b border-border px-4 py-1.5 lg:sticky lg:top-0 lg:z-40 flex-shrink-0" ref={controlsContainerRef}>
            <div ref={menuContainerRef} className="flex flex-col md:flex-row md:flex-wrap items-center gap-1 md:gap-x-7 md:gap-y-0.5">

              {/* Ticker / Stock Selector */}
              {showTicker && (
                <div className="relative stock-dropdown-container flex items-center gap-2 w-full md:w-auto">
                  <label className="text-sm font-medium whitespace-nowrap">Ticker:</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={stockInput}
                      onChange={(e) => {
                        const val = e.target.value.toUpperCase();
                        setStockInput(val);
                        setShowStockSuggestions(true);
                        if (STOCK_LIST.includes(val)) {
                          setSymbol(val);
                        }
                      }}
                      onFocus={() => setShowStockSuggestions(true)}
                      placeholder="Search ticker..."
                      className="h-9 w-28 md:w-32 px-3 border border-[#3a4252] rounded-md bg-background text-foreground text-sm font-mono uppercase focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                    {showStockSuggestions && (
                      <div className="absolute top-full left-0 mt-1 w-44 max-h-60 overflow-y-auto bg-card border border-border rounded-md shadow-xl z-50 py-1">
                        {filteredStocks.slice(0, 25).map(stk => (
                          <button
                            key={stk}
                            type="button"
                            onClick={() => {
                              setStockInput(stk);
                              setSymbol(stk);
                              setShowStockSuggestions(false);
                            }}
                            className={`w-full px-3 py-1.5 text-left text-xs font-mono hover:bg-accent transition-colors ${stk === symbol ? 'bg-primary/20 text-primary font-bold' : 'text-foreground'}`}
                          >
                            {stk}
                          </button>
                        ))}
                        {filteredStocks.length === 0 && (
                          <div className="px-3 py-2 text-xs text-muted-foreground">No stock found</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Chart Style Selector */}
              <div className="flex items-center gap-2 w-full md:w-auto">
                <label className="text-sm font-medium whitespace-nowrap">Style:</label>
                <select
                  value={style}
                  onChange={(e) => setStyle(e.target.value as ChartStyle)}
                  className="h-9 px-3 border border-[#3a4252] rounded-md bg-background text-foreground text-sm w-full md:w-auto"
                >
                  <option value="line">Line</option>
                  <option value="candles">Candles</option>
                </select>
              </div>

              {/* Timeframe Selector */}
              <div className="flex items-center gap-2 w-full md:w-auto">
                <label className="text-sm font-medium whitespace-nowrap">Timeframe:</label>
                <select
                  value={timeframe}
                  onChange={(e) => {
                    const newTimeframe = e.target.value;
                    setIsUserControlled(true);
                    setTimeframe(newTimeframe as Timeframe);
                    if (onTimeframeChange) {
                      onTimeframeChange(newTimeframe);
                    }
                  }}
                  className="h-9 px-3 border border-[#3a4252] rounded-md bg-background text-foreground text-sm w-full md:w-auto"
                >
                  {effectiveTimeframeOptions.map(tf => (
                    <option key={tf} value={tf}>{getTimeframeLabel(tf)}</option>
                  ))}
                </select>
              </div>

              {/* Indicators Button */}
              <button
                onClick={() => setShowIndicatorSettings(true)}
                className="h-[34px] px-4 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-medium whitespace-nowrap flex items-center justify-center gap-2 w-full md:w-auto"
              >
                <BarChart2 className="w-4 h-4" />
                <span>Indicators</span>
              </button>

              {/* Settings Button */}
              {profile?.role === 'developer' && (
                <button
                  onClick={() => setShowSettings(true)}
                  className="h-[34px] px-4 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-medium whitespace-nowrap flex items-center justify-center gap-2 w-full md:w-auto"
                >
                  <SettingsIcon className="w-4 h-4" />
                  <span>Settings</span>
                </button>
              )}
            </div>
          </div>
        )}
        <div className="flex-1 w-full relative min-h-0 flex flex-col" style={{ padding: 0, margin: 0 }}>
        {/* Debug info */}
        {/* <div className="absolute top-2 right-2 z-20 bg-background/80 backdrop-blur-sm border border-border rounded px-2 py-1 text-xs">
          <div>Viewport: {chartViewportHeight}px</div>
          <div>Data: {filter{/* <div className="absolute top-2 right-2 z-20 bg-background/80 backdrop-blur-sm border border-border rounded px-2 py-1 text-xs">
          <div>Viewport: {chartViewportHeight}px</div>
          <div>Data: {filteredRows.length} rows</div>
          <div>Loading: {isLoadingData ? 'Yes' : 'No'}</div>
        </div>edRows.length} rows</div>
          <div>Loading: {isLoadingData ? 'Yes' : 'No'}</div>
        </div> */}
        {/* Loading overlay */}
        {isLoadingData && (
          <div className="absolute inset-0 z-10 p-4 bg-card overflow-hidden">
            <div className="animate-pulse flex flex-col h-full border border-border rounded-lg">
              {/* Header skeleton */}
              <div className="flex justify-between items-center p-3 border-b border-border/50 bg-muted/20">
                <div className="flex gap-3">
                  <div className="h-5 w-20 bg-slate-700/20 dark:bg-slate-700/50 rounded"></div>
                  <div className="h-5 w-16 bg-slate-700/20 dark:bg-slate-700/50 rounded"></div>
                </div>
                <div className="flex gap-2">
                  <div className="h-5 w-8 bg-slate-700/20 dark:bg-slate-700/50 rounded"></div>
                  <div className="h-5 w-8 bg-slate-700/20 dark:bg-slate-700/50 rounded"></div>
                </div>
              </div>
              {/* Chart body skeleton */}
              <div className="flex-1 relative flex">
                <div className="flex-1 relative">
                  <div className="absolute inset-0 flex flex-col justify-between py-8 opacity-20">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="w-full h-px bg-slate-700/50 dark:bg-slate-500/50"></div>
                    ))}
                  </div>
                  {/* Candlestick lines */}
                  <div className="absolute inset-x-4 bottom-8 top-8 flex items-end justify-between opacity-40">
                    {[...Array(24)].map((_, i) => (
                      <div key={i} className="w-1.5 bg-slate-700/30 dark:bg-slate-700/60 rounded-t" style={{ height: `${20 + Math.random() * 70}%` }}></div>
                    ))}
                  </div>
                </div>
                {/* Y-axis skeleton */}
                <div className="w-14 border-l border-border/50 flex flex-col justify-between py-8 px-2 opacity-30">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="w-8 h-2 bg-slate-700/50 dark:bg-slate-500/50 rounded"></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error overlay */}
        {!isLoadingData && rows.length === 0 && err && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3 text-center p-6">
              <div className="text-4xl">📊</div>
              <div className="text-sm text-muted-foreground">
                Error loading data: {err}
              </div>
              <button
                onClick={() => {
                  setSymbol(symbol); // Trigger reload
                }}
                className="px-4 py-2 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Foreign/Domestic cursor-following legend — filled & positioned via direct DOM
            manipulation on crosshair move (see handleFdCrosshairMove). The chart's native
            series title can't safely be updated on crosshair move — that causes infinite
            recursion in lightweight-charts v5 — so we track the cursor with our own element. */}
        <div
          ref={foreignDomesticLegendRef}
          style={{ position: 'absolute', display: 'none', pointerEvents: 'none', left: 0, top: 0, width: 0, height: 0 }}
        />
        {/* BaZi Indicator cursor-following tooltip — filled & positioned via direct DOM manipulation */}
        <div
          ref={baziTooltipRef}
          style={{ position: 'absolute', display: 'none', pointerEvents: 'none', left: 0, top: 0, width: 0, height: 0 }}
        />
        {!hideControls && !isLoadingData && rows.length > 0 && (
          <div className={`absolute top-1 ${hasLeftAxis ? 'left-[60px]' : 'left-2'} z-10 bg-transparent px-1 py-0.5 flex flex-col pointer-events-none select-none gap-y-0.5 transition-all duration-200`}>
            {/* Symbol info, timeframe & Price info inline on single row */}
            <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 text-xs text-muted-foreground font-sans border border-transparent px-1.5 py-0.5 rounded transition-all duration-200 cursor-default pointer-events-none">
              <span className="font-bold text-card-foreground text-sm uppercase tracking-wide whitespace-nowrap" title={companyName || symbol}>
                {companyName || symbol}
              </span>
              <span>·</span>
              <span className="font-mono font-medium text-foreground">
                {latest ? latest.close.toLocaleString() : '-'}
              </span>
              <span className={`font-mono font-medium ${chg >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {chg >= 0 ? '+' : ''}{chg.toFixed(0)} ({chgPct.toFixed(2)}%)
              </span>
            </div>

            {/* Collapse/Expand button and Indicators list */}
            <div className="mt-0.5 flex flex-col items-start gap-1 pointer-events-none">
              {/* If we have indicators active */}
              {indicators.filter(ind => ind.enabled && !isIndicatorSeparate(ind)).length > 0 && (
                <>
                  {/* Indicators List */}
                  {!isIndicatorsListMinimized && (
                    <div className="flex flex-col gap-0.5 mt-0.5 w-full pointer-events-none">
                      {indicators.filter(ind => ind.enabled && !isIndicatorSeparate(ind)).map(ind => (
                        <div
                          key={ind.id}
                          className="flex items-center justify-start gap-2 py-0.5 px-2 rounded border border-transparent text-[11px] text-muted-foreground/80 transition-all duration-200 group cursor-default w-fit pointer-events-none"
                        >
                          <span className={`font-medium whitespace-nowrap ${ind.hidden ? 'text-white/40' : 'text-white'}`}>
                            {ind.type === 'vertical_line_15d' || ind.name === 'Vertical Lines (+15D)' ? '15-day Cycle' : (ind.name === 'VOLUME_HISTOGRAM' ? 'Volume Histogram' : (ind.type === 'bazi_daily' ? 'BaZi Daily' : (ind.type === 'bazi_monthly' ? 'BaZi Monthly' : (ind.name === 'bazi_indicator' || ind.type === 'bazi_indicator' ? 'BaZi Daily' : (ind.name === 'Foreign' ? 'Foreign Flow' : ind.name)))))}
                          </span>
                          <div className="flex items-center gap-1.5 opacity-60 group-hover:opacity-100 transition-opacity pointer-events-auto">
                            {/* Eye icon to toggle visibility */}
                            <button
                              onClick={() => {
                                setIndicators(prev => prev.map(i => 
                                  i.id === ind.id ? { ...i, hidden: !i.hidden } : i
                                ));
                              }}
                              className="hover:text-foreground transition-colors"
                              title={ind.hidden ? "Show" : "Hide"}
                            >
                              {ind.hidden ? (
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                </svg>
                              ) : (
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              )}
                            </button>
                            {ind.type === 'vertical_line_15d' && (
                              <button
                                onClick={() => {
                                  if (placingIndicatorId === ind.id) {
                                    setPlacingIndicatorId(null);
                                    lastPreviewTimeRef.current = null;
                                    setPreviewItems([]);
                                  } else {
                                    setPlacingIndicatorId(ind.id);
                                  }
                                }}
                                className={`transition-colors ${placingIndicatorId === ind.id ? 'text-foreground animate-pulse bg-slate-800/80 p-0.5 rounded' : 'hover:text-foreground transition-colors'}`}
                                title={placingIndicatorId === ind.id ? "Batal Pindahkan T0" : "Pindahkan Posisi T0 (Klik Chart)"}
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                                </svg>
                              </button>
                            )}
                            {/* Settings icon */}
                            <button
                              onClick={() => editIndicator(ind)}
                              className="hover:text-foreground transition-colors"
                              title="Settings"
                            >
                              <SettingsIcon className="w-3 h-3" />
                            </button>
                            {/* Close icon */}
                            <button
                              onClick={() => removeIndicator(ind.id)}
                              className="hover:text-destructive transition-colors"
                              title="Remove"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Minimize Toggle Button at bottom */}
                  <button
                    onClick={() => setIsIndicatorsListMinimized(!isIndicatorsListMinimized)}
                    className="flex items-center justify-center gap-1 text-[10px] text-muted-foreground/80 hover:text-foreground transition-all duration-200 py-0.5 px-2 bg-transparent hover:bg-slate-950/80 border border-transparent hover:border-border/30 rounded select-none font-mono mt-1 w-fit cursor-default pointer-events-auto"
                    title={isIndicatorsListMinimized ? "Show indicator legend" : "Hide indicator legend"}
                  >
                    {isIndicatorsListMinimized ? (
                      <>
                        <ChevronDown className="w-3 h-3" />
                        <span>{indicators.filter(ind => ind.enabled && !ind.separateScale).length}</span>
                      </>
                    ) : (
                      <ChevronUp className="w-3 h-3" />
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Price overlay for Dashboard - minimal version */}
        {hideControls && !isLoadingData && rows.length > 0 && showStockSymbol && (
          <div className={`absolute top-1 ${hasLeftAxis ? 'left-[60px]' : 'left-2'} z-10 bg-transparent px-1.5 py-0.5 rounded border border-transparent flex flex-col pointer-events-none select-none transition-all duration-200`}>
            {/* Stock Symbol and Timeframe Row */}
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="font-bold text-card-foreground uppercase tracking-wide" style={{ fontSize: '1.15em' }}>
                {symbol}
              </div>
              {timeframeOptions && timeframeOptions.length > 0 && onTimeframeChange && (
                <select
                  value={timeframe}
                  onChange={(e) => {
                    const newTimeframe = e.target.value;
                    setIsUserControlled(true);
                    setTimeframe(newTimeframe as Timeframe);
                    onTimeframeChange(newTimeframe);
                  }}
                  className="text-xs sm:text-sm bg-transparent text-foreground focus:outline-none focus:ring-2 focus:ring-primary rounded pointer-events-auto"
                  title="Timeframe"
                  aria-label="Timeframe"
                >
                  {timeframeOptions.map(tf => (
                    <option key={tf} value={tf}>{getTimeframeLabel(tf)}</option>
                  ))}
                </select>
              )}
            </div>
            {/* Price Info */}
            <div className="font-mono text-xs sm:text-sm font-medium">{latest ? latest.close.toLocaleString() : '-'}</div>
            <div className={`text-xs sm:text-sm font-medium ${chg >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {chg >= 0 ? '+' : ''}{chg.toFixed(0)} ({chgPct.toFixed(2)}%)
            </div>
          </div>
        )}

        <ChartErrorBoundary>
          {!isLoadingData && rows.length > 0 && (
            <>
              {style === 'footprint' ? (
                <div className="w-full flex-1 min-h-0" style={{ padding: 0, margin: 0 }}>
                  <FootprintChart
                    showCrosshair={footprintSettings.showCrosshair}
                    showPOC={footprintSettings.showPOC}
                    showDelta={footprintSettings.showDelta}
                    timeframe={footprintSettings.timeframe}
                    zoom={footprintSettings.zoom}
                    verticalScaleProp={verticalScale}
                    onVerticalScaleChange={setVerticalScale}
                    horizontalScaleProp={horizontalScale}
                    onHorizontalScaleChange={setHorizontalScale}
                    stockCode={symbol}
                    date={new Date().toISOString().split('T')[0]?.replace(/-/g, '') || ''}
                    ohlc={filteredRows.length > 0 ? filteredRows.map(d => ({
                      timestamp: new Date((d.time || 0) * 1000).toISOString(),
                      open: d.open,
                      high: d.high,
                      low: d.low,
                      close: d.close
                    })) : []}
                  />
                </div>
              ) : (
                <>
                  <div
                    ref={containerRef}
                    className="w-full flex-1 min-h-0"
                    style={{ padding: 0, margin: 0 }}
                    onClick={handleChartClick}
                    onMouseMove={handleMouseMove}
                  />
                </>
              )}
            </>
          )}
        </ChartErrorBoundary>
      </div>
    </Card>

      {/* Separate indicator charts */}
      {indicators.filter(ind => ind.enabled && isIndicatorSeparate(ind)).map(indicator => (
        <Card key={indicator.id} className="p-0 relative indicator-card">
          <div className="p-2 border-b border-border indicator-header">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: indicator.color }}
                />
                <span className="text-sm font-medium">
                  {indicator.name === 'VOLUME_HISTOGRAM' ? 'Volume Histogram' : (indicator.name === 'bazi_indicator' || indicator.type === 'bazi_indicator' ? 'BaZi Indicator' : (indicator.name === 'Foreign' ? 'Foreign Flow' : indicator.name))}
                </span>
                <span className="text-xs text-muted-foreground">Separate Chart</span>
              </div>
              <div className="flex items-center gap-1">
                {indicator.type === 'rsi' && (
                  <button
                    onClick={() => {
                      setSelectedIndicatorForSettings(indicator);
                      setShowIndividualSettings(true);
                    }}
                    className="px-2 py-1 text-xs border border-border rounded hover:bg-accent"
                    title="RSI Settings"
                  >
                    ⚙️
                  </button>
                )}
                <button
                  onClick={() => toggleIndicator(indicator.id)}
                  className="px-2 py-1 text-xs border border-border rounded hover:bg-accent"
                  title={indicator.enabled ? "Hide indicator" : "Show indicator"}
                >
                  {indicator.enabled ? "👁️" : "🚫👁️"}
                </button>
                <button
                  onClick={() => removeIndicator(indicator.id)}
                  className="px-2 py-1 text-xs border border-red-500 text-red-500 rounded hover:bg-red-50"
                  title="Delete"
                >
                  🗑️
                </button>
              </div>
            </div>
          </div>
          {indicator.enabled && (
            <div
              ref={(el) => {
                if (el) {
                  indicatorContainerRefs.current[indicator.id] = el;
                }
              }}
              className="w-full relative indicator-chart-container"
              style={{
                height: `${isBaZiType(indicator.type) ? Math.max(50, Math.round(indicatorChartHeight * 0.5)) : indicatorChartHeight}px`,
                minHeight: `${isBaZiType(indicator.type) ? Math.max(50, Math.round(indicatorChartHeight * 0.5)) : indicatorChartHeight}px`
              }}
            >
              {/* Loading overlay for indicator charts */}
              {(isLoadingData || (indicator.type === 'buy_sell_frequency' && bidAskData.length === 0) || !indicatorSeriesReady[indicator.id]) && (
                <div className="absolute inset-0 z-10 p-2 bg-card overflow-hidden">
                  <div className="animate-pulse flex flex-col h-full border border-border rounded-lg">
                    <div className="flex justify-between items-center p-2 border-b border-border/50 bg-muted/10">
                      <div className="h-4 w-24 bg-slate-700/20 dark:bg-slate-700/50 rounded"></div>
                      <div className="h-4 w-12 bg-slate-700/20 dark:bg-slate-700/50 rounded"></div>
                    </div>
                    <div className="flex-1 relative flex">
                      <div className="flex-1 relative">
                        <div className="absolute inset-0 flex flex-col justify-between py-2 opacity-20">
                          {[...Array(3)].map((_, i) => (
                            <div key={i} className="w-full h-px bg-slate-700/50 dark:bg-slate-500/50"></div>
                          ))}
                        </div>
                        {/* Histogram skeleton */}
                        <div className="absolute inset-x-2 bottom-0 h-full flex items-end justify-between opacity-40">
                          {[...Array(24)].map((_, i) => (
                            <div key={i} className="w-1.5 bg-slate-700/30 dark:bg-slate-700/60 rounded-t" style={{ height: `${10 + Math.random() * 80}%` }}></div>
                          ))}
                        </div>
                      </div>
                      <div className="w-14 border-l border-border/50 flex flex-col justify-between py-2 px-2 opacity-30">
                        {[...Array(3)].map((_, i) => (
                          <div key={i} className="w-8 h-2 bg-slate-700/50 dark:bg-slate-500/50 rounded"></div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      ))}

      {/* Settings Popup */}
      {showSettings && (
        <div className="fixed inset-0 flex items-center justify-center z-40">
          <div className="bg-background border border-border rounded-lg shadow-lg p-4 w-80 max-w-[90vw]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold">Chart Colors</h3>
              <button
                onClick={() => setShowSettings(false)}
                className="text-muted-foreground hover:text-foreground text-xs"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              {/* Line Chart Colors (Area Chart) */}
              {style === 'line' && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground">Line Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={chartColors.area.line}
                        onChange={(e) => setChartColors(prev => ({
                          ...prev,
                          area: { ...prev.area, line: e.target.value }
                        }))}
                        className="w-8 h-6 border border-border rounded cursor-pointer"
                      />
                      <span className="text-xs text-muted-foreground font-mono">{chartColors.area.line}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground">Top Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={chartColors.area.top}
                        onChange={(e) => setChartColors(prev => ({
                          ...prev,
                          area: { ...prev.area, top: e.target.value }
                        }))}
                        className="w-8 h-6 border border-border rounded cursor-pointer"
                      />
                      <span className="text-xs text-muted-foreground font-mono">{chartColors.area.top}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground">Bottom Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={chartColors.area.bottom}
                        onChange={(e) => setChartColors(prev => ({
                          ...prev,
                          area: { ...prev.area, bottom: e.target.value }
                        }))}
                        className="w-8 h-6 border border-border rounded cursor-pointer"
                      />
                      <span className="text-xs text-muted-foreground font-mono">{chartColors.area.bottom}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Candlestick Colors */}
              {style === 'candles' && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground">Bullish (Up)</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={chartColors.candles.up}
                        onChange={(e) => setChartColors(prev => ({
                          ...prev,
                          candles: { ...prev.candles, up: e.target.value, wickUp: e.target.value }
                        }))}
                        className="w-8 h-6 border border-border rounded cursor-pointer"
                      />
                      <span className="text-xs text-muted-foreground font-mono">{chartColors.candles.up}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground">Bearish (Down)</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={chartColors.candles.down}
                        onChange={(e) => setChartColors(prev => ({
                          ...prev,
                          candles: { ...prev.candles, down: e.target.value, wickDown: e.target.value }
                        }))}
                        className="w-8 h-6 border border-border rounded cursor-pointer"
                      />
                      <span className="text-xs text-muted-foreground font-mono">{chartColors.candles.down}</span>
                    </div>
                  </div>
                </div>
              )}


              {/* Footprint Chart Settings */}
              {style === 'footprint' && (
                <div className="space-y-3 border-t border-border pt-3">
                  <h4 className="text-xs font-medium text-muted-foreground">Footprint Chart Settings</h4>

                  {/* Display Controls */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs text-muted-foreground">Show Crosshair</label>
                      <input
                        type="checkbox"
                        checked={footprintSettings.showCrosshair}
                        onChange={(e) => setFootprintSettings(prev => ({ ...prev, showCrosshair: e.target.checked }))}
                        className="w-3 h-3"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="text-xs text-muted-foreground">Show POC</label>
                      <input
                        type="checkbox"
                        checked={footprintSettings.showPOC}
                        onChange={(e) => setFootprintSettings(prev => ({ ...prev, showPOC: e.target.checked }))}
                        className="w-3 h-3"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="text-xs text-muted-foreground">Show Delta</label>
                      <input
                        type="checkbox"
                        checked={footprintSettings.showDelta}
                        onChange={(e) => setFootprintSettings(prev => ({ ...prev, showDelta: e.target.checked }))}
                        className="w-3 h-3"
                      />
                    </div>
                  </div>

                  {/* Timeframe Selection */}
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground">Timeframe</label>
                    <select
                      value={footprintSettings.timeframe}
                      onChange={(e) => setFootprintSettings(prev => ({ ...prev, timeframe: e.target.value }))}
                      className="w-full px-2 py-1 text-xs border border-border rounded bg-background text-foreground"
                    >
                      <option value="1m">1 Minute</option>
                      <option value="5m">5 Minutes</option>
                      <option value="15m">15 Minutes</option>
                      <option value="30m">30 Minutes</option>
                      <option value="1h">1 Hour</option>
                      <option value="4h">4 Hours</option>
                      <option value="1d">1 Day</option>
                    </select>
                  </div>

                  {/* Zoom Control */}
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground">Zoom Level</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="range"
                        min="0.5"
                        max="3"
                        step="0.1"
                        value={footprintSettings.zoom}
                        onChange={(e) => setFootprintSettings(prev => ({ ...prev, zoom: parseFloat(e.target.value) }))}
                        className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <span className="text-xs text-muted-foreground font-mono w-12 text-right">
                        {footprintSettings.zoom.toFixed(1)}x
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Chart Height Control */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground">Indicator Chart Height</label>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min="50"
                    max="200"
                    value={indicatorChartHeight}
                    onChange={(e) => setIndicatorChartHeight(parseInt(e.target.value))}
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <span className="text-xs text-muted-foreground font-mono w-12 text-right">
                    {indicatorChartHeight}px
                  </span>
                </div>
              </div>


              {/* Action Buttons */}
              <div className="pt-2 border-t border-border">
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setChartColors({
                        line: '#2563eb',
                        candles: { up: '#16a34a', down: '#dc2626', wickUp: '#16a34a', wickDown: '#dc2626' },
                        area: { line: '#2563eb', top: 'rgba(37,99,235,0.20)', bottom: 'rgba(37,99,235,0.05)' }
                      });
                      setIndicatorChartHeight(100);
                    }}
                    className="px-3 py-1 text-xs border border-border rounded hover:bg-accent"
                  >
                    Reset Default
                  </button>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Technical Indicators Manager Popup */}
      {showIndicatorSettings && (
        <div className="fixed inset-0 flex items-center justify-center z-40">
          <div className="bg-background border border-border rounded-lg shadow-lg p-5 w-[460px] max-w-[95vw]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold">Technical Indicators</h3>
              <button
                onClick={() => setShowIndicatorSettings(false)}
                className="text-muted-foreground hover:text-foreground text-sm"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* Add New Indicator */}
              <div className="space-y-3">
                <h4 className="text-xs font-medium text-muted-foreground">Add New Indicator</h4>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  <button
                    onClick={() => addIndicator('ma', '#45b7d1', false, 'simple')}
                    className="w-full px-3 py-2 text-xs border border-border rounded hover:bg-accent text-left"
                  >
                    Moving Average (MA)
                  </button>
                  <button
                    onClick={() => addIndicator('rsi', '#6c5ce7', true)}
                    className="w-full px-3 py-2 text-xs border border-border rounded hover:bg-accent text-left"
                  >
                    Relative Strength Index
                  </button>
                  <button
                    onClick={() => addIndicator('macd', '#e17055', true)}
                    className="w-full px-3 py-2 text-xs border border-border rounded hover:bg-accent text-left"
                  >
                    Moving Average Convergence Divergence
                  </button>
                  <button
                    onClick={() => addIndicator('stochastic', '#9b59b6', true)}
                    className="w-full px-3 py-2 text-xs border border-border rounded hover:bg-accent text-left"
                  >
                    Stochastic Oscillator
                  </button>
                  <button
                    onClick={() => addIndicator('volume_histogram', '#e67e22', false)}
                    className="w-full px-3 py-2 text-xs border border-border rounded hover:bg-accent text-left"
                  >
                    Volume Histogram
                  </button>
                  <button
                    onClick={() => addIndicator('buy_sell_frequency', '#8e44ad', true)}
                    className="w-full px-3 py-2 text-xs border border-border rounded hover:bg-accent text-left"
                  >
                    Buy / Sell Frequency
                  </button>
                  {enableForeignDomestic && (
                    <button
                      onClick={() => addIndicator('foreign_domestic', '#3b82f6', false)}
                      className="w-full px-3 py-2 text-xs border border-border rounded hover:bg-accent text-left"
                    >
                      Foreign Flow
                    </button>
                  )}
                  {(profile?.role === 'developer' || profile?.role === 'admin') && (
                    <button
                      onClick={() => addIndicator('vertical_line_15d', '#8b5cf6', false)}
                      className="w-full px-3 py-2 text-xs border border-border rounded hover:bg-accent text-left flex items-center justify-between"
                    >
                      <span>15-day Cycle</span>
                      <span className="text-[10px] bg-primary/20 text-primary px-1.5 py-0.5 rounded font-mono">Dev/Admin</span>
                    </button>
                  )}
                  {(profile?.role === 'developer' || profile?.role === 'admin') && (
                    <>
                      <button
                        onClick={() => addIndicator('bazi_daily', '#16a34a', false)}
                        className="w-full px-3 py-2 text-xs border border-border rounded hover:bg-accent text-left flex items-center justify-between"
                      >
                        <span>BaZi Daily</span>
                        <span className="text-[10px] bg-primary/20 text-primary px-1.5 py-0.5 rounded font-mono">Dev/Admin</span>
                      </button>
                      <button
                        onClick={() => addIndicator('bazi_monthly', '#3b82f6', false)}
                        className="w-full px-3 py-2 text-xs border border-border rounded hover:bg-accent text-left flex items-center justify-between"
                      >
                        <span>BaZi Monthly</span>
                        <span className="text-[10px] bg-primary/20 text-primary px-1.5 py-0.5 rounded font-mono">Dev/Admin</span>
                      </button>
                    </>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Individual Indicator Settings Popup */}
      {showIndividualSettings && selectedIndicatorForSettings && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-background border border-border rounded-lg shadow-lg p-4 w-80 max-w-[90vw]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold">{selectedIndicatorForSettings.name} Settings</h3>
              <button
                onClick={() => {
                  setShowIndividualSettings(false);
                  setSelectedIndicatorForSettings(null);
                }}
                className="text-muted-foreground hover:text-foreground text-xs"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* RSI Settings */}
              {selectedIndicatorForSettings.type === 'rsi' && (
                <div className="space-y-3">
                  <h4 className="text-xs font-medium text-muted-foreground">RSI Reference Lines</h4>

                  {/* Overbought Line */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs text-muted-foreground">Overbought Line</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={rsiSettings.showOverbought}
                          onChange={(e) => setRsiSettings(prev => ({ ...prev, showOverbought: e.target.checked }))}
                          className="w-3 h-3"
                        />
                        <span className="text-xs text-muted-foreground">Show</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="range"
                        min="60"
                        max="90"
                        value={rsiSettings.overbought}
                        onChange={(e) => setRsiSettings(prev => ({ ...prev, overbought: parseInt(e.target.value) }))}
                        className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        disabled={!rsiSettings.showOverbought}
                      />
                      <span className="text-xs text-muted-foreground font-mono w-8 text-right">
                        {rsiSettings.overbought}
                      </span>
                    </div>
                  </div>

                  {/* Oversold Line */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs text-muted-foreground">Oversold Line</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={rsiSettings.showOversold}
                          onChange={(e) => setRsiSettings(prev => ({ ...prev, showOversold: e.target.checked }))}
                          className="w-3 h-3"
                        />
                        <span className="text-xs text-muted-foreground">Show</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="range"
                        min="10"
                        max="40"
                        value={rsiSettings.oversold}
                        onChange={(e) => setRsiSettings(prev => ({ ...prev, oversold: parseInt(e.target.value) }))}
                        className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        disabled={!rsiSettings.showOversold}
                      />
                      <span className="text-xs text-muted-foreground font-mono w-8 text-right">
                        {rsiSettings.oversold}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="pt-2 border-t border-border">
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setRsiSettings({
                        overbought: 70,
                        oversold: 30,
                        showOverbought: true,
                        showOversold: true
                      });
                    }}
                    className="px-3 py-1 text-xs border border-border rounded hover:bg-accent"
                  >
                    Reset Default
                  </button>
                  <button
                    onClick={() => {
                      setShowIndividualSettings(false);
                      setSelectedIndicatorForSettings(null);
                    }}
                    className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Indicator Editor Modal */}
      {showIndicatorEditor && editingIndicator && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-background border border-border rounded-lg shadow-lg p-6 w-96 max-w-[90vw]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Edit Indicator</h3>
              <button
                onClick={() => setShowIndicatorEditor(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                ✕
              </button>
            </div>

            <IndicatorEditor
              indicator={editingIndicator}
              onSave={updateIndicator}
              onCancel={() => setShowIndicatorEditor(false)}
              volumeHistogramSettings={volumeHistogramSettings}
              setVolumeHistogramSettings={setVolumeHistogramSettings}
            />
          </div>
        </div>
      )}
    </div>
  );
});




